import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Group,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Tabs,
  Title,
} from '@mantine/core'
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
  type ChartOptions,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { Line } from 'react-chartjs-2'
import { PageShell } from '@/components/ui'
import { IconCalendar, IconFolder, IconRefresh, IconUser } from '@/components/icons'
import { OperatorTicketsModal, type OperatorTicketsBlock } from '@/components/dashboard/OperatorTicketsModal'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth-context'
import {
  buildDashboardPeriodParams,
  resolveDashboardPeriodParams,
  getDashboardPeriodLabel,
  getPreviousPeriodParams,
  type DashboardPeriodKey,
} from '@/lib/dashboard-period'
import { periodParamsToAnalyticsQuery } from '@/lib/analytics-query'
import { resolveUploadsUrl } from '@/lib/uploads'
import { isAIOperator, resolveOperatorAvatarSrc } from '@/lib/dashboard-ai-operator'
import {
  defaultPermissionsForRole,
  hasPermission,
  loadMyPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import { AnalyticsPeriodFilters } from '@/pages/analytics/AnalyticsPeriodFilters'
import {
  buildAiProjectChartData,
  buildAiReasonChartData,
  buildOperatorChartData,
  buildOperatorKtuChartData,
  buildProductChartData,
  buildProjectTimeChartData,
  buildSourceChartData,
} from '@/pages/analytics/analytics-chart-data'
import { HourlyDistributionCard, type HourlyBasis } from '@/pages/analytics/analytics-hourly-distribution'
import {
  AnalyticsChartCard,
  AiTransferTicketsModal,
  AnalyticsTooltipContent,
  AnalyticsTooltipProvider,
  AxisIconRow,
  BlockLoader,
  ChartRowsTooltipContent,
  LegendAiReasonRow,
  LegendAiProjectRow,
  LegendKtuRow,
  LegendOperatorRow,
  LegendProductRow,
  LegendSourceRow,
  MainInfoCard,
  MainInfoSummary,
  ProductChartTooltipContent,
  ProductTicketsModal,
} from '@/pages/analytics/analytics-new-widgets'
import {
  ANALYTICS_TOOLTIP_WIDTH,
  PRODUCT_TOOLTIP_HEIGHT,
  PROJECT_RESOLUTION_TIME_SOLID,
  PROJECT_RESPONSE_TIME_SOLID,
  aiReasonHints,
  aiReasonLabels,
  buildProjectSourceChartData,
  aggregateTiming,
  aggregateSlaTimeline,
  changeLine,
  channelSourceKey,
  channelSourceLabel,
  chartTooltipBase,
  compactChangeLine,
  formatDurationClock,
  formatKtu,
  formatMinutes,
  formatNumber,
  formatPercent,
  normalizeChannelStats,
  normalizeProjectChannelStats,
  operatorColors,
  operatorKey,
  previousColor,
  productKey,
  sourceColors,
  toLocalDatetimeValue,
  type AiReasonKey,
  type ChartRowsTooltipData,
  type ProductStatsRow,
  type ProductTooltipData,
} from '@/pages/analytics/analytics-new-utils'
import type {
  AnalyticsDashboardStats,
  AiOperationsAnalyticsResponse,
  MyPermissionsResponse,
  OperatorKtuRow,
  OperatorOverviewRow,
  ProjectRow,
  SlaBreakdownRow,
  SlaTimelinePoint,
} from '@/types/api'

ChartJS.register(BarController, CategoryScale, LinearScale, BarElement, LineController, LineElement, PointElement, ChartTooltip, Legend)

type SlaMetric = NonNullable<NonNullable<AnalyticsDashboardStats['sla']>['first_response']>
type SlaTone = 'danger' | 'warning' | 'success'
type SlaFilterKey = 'timeline' | 'product' | 'channel' | 'queue' | 'agent'
type SlaLocalFilter = { period: DashboardPeriodKey; projectIds: string[] }

const SLA_BLUE = '#5B5CE2'
const SLA_RED = '#E11912'
const SLA_YELLOW = '#F2B51D'
const SLA_GREEN = '#009B2D'
const SLA_BORDER = '#E4E8F3'
const SLA_SOFT = '#F6F7FC'
const SLA_PERIOD_OPTIONS: { value: DashboardPeriodKey; label: string }[] = [
  { value: '1h', label: 'За час' },
  { value: 'today', label: 'Сегодня' },
  { value: '24h', label: 'За сутки' },
  { value: '7', label: 'За неделю' },
  { value: '30', label: 'За 30 дней' },
  { value: '90', label: 'За 90 дней' },
  { value: 'custom_datetime', label: 'Свой период' },
]

function slaPercent(value: number | null | undefined, digits = 1) {
  if (value == null || !Number.isFinite(Number(value))) return '—'
  return `${Number(value).toFixed(digits).replace('.', ',')}%`
}

function slaTone(value: number | null | undefined): SlaTone {
  const n = Number(value ?? 0)
  if (n >= 85) return 'success'
  if (n >= 60) return 'warning'
  return 'danger'
}

function slaColor(value: number | null | undefined) {
  const tone = slaTone(value)
  if (tone === 'success') return SLA_BLUE
  if (tone === 'warning') return SLA_YELLOW
  return SLA_RED
}

function slaDeltaText(current: number | null | undefined, previous: number | null | undefined, unit: 'percent' | 'count' | 'minutes' = 'percent') {
  if (current == null || previous == null || !Number.isFinite(Number(current)) || !Number.isFinite(Number(previous))) return '—'
  const diff = Number(current) - Number(previous)
  const abs = Math.abs(diff)
  if (abs < 0.05) return 'без изменений'
  const amount = unit === 'minutes' ? formatMinutes(abs) : unit === 'count' ? formatNumber(abs) : `${abs.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} п.п.`
  return `на ${amount} ${diff > 0 ? 'выше' : 'ниже'} предыдущего периода`
}

function targetLabel(minutes: number | null | undefined, withPrefix = false) {
  const text = formatMinutes(minutes)
  if (text === '—') return text
  return withPrefix ? `до ${text}.` : `${text}.`
}

function SlaFilterStrip({
  period,
  onPeriodChange,
  projectIds,
  onProjectIdsChange,
  projects,
  onRefresh,
  loading,
  showSort = false,
}: {
  period: DashboardPeriodKey
  onPeriodChange: (value: DashboardPeriodKey) => void
  projectIds: string[]
  onProjectIdsChange: (ids: string[]) => void
  projects: ProjectRow[]
  onRefresh: () => void
  loading: boolean
  showSort?: boolean
}) {
  return (
    <Group gap={8} wrap="nowrap" align="center">
      <Select
        size="xs"
        aria-label="Период SLA"
        data={SLA_PERIOD_OPTIONS}
        value={period}
        onChange={(value) => value && onPeriodChange(value as DashboardPeriodKey)}
        leftSection={<IconCalendar size={13} />}
        allowDeselect={false}
        styles={{
          input: { height: 32, minHeight: 32, width: 122, background: '#F5F6FA', border: 0, borderRadius: 6, color: '#111827', fontSize: 12 },
          section: { color: '#111827' },
        }}
      />
      <Select
        size="xs"
        aria-label="Проект SLA"
        data={projects.map((project) => ({ value: project.id, label: project.name || project.slug || project.id }))}
        value={projectIds[0] || null}
        onChange={(value) => onProjectIdsChange(value ? [value] : [])}
        leftSection={<IconFolder size={13} />}
        placeholder="Все проекты"
        searchable
        clearable
        nothingFoundMessage="Нет"
        styles={{
          input: { height: 32, minHeight: 32, width: 150, background: '#F5F6FA', border: 0, borderRadius: 6, color: '#111827', fontSize: 12 },
          section: { color: '#111827' },
        }}
      />
      {showSort ? (
        <Select
          size="xs"
          aria-label="Сортировка SLA"
          value="name"
          data={[{ value: 'name', label: 'По имени' }]}
          allowDeselect={false}
          styles={{ input: { height: 32, minHeight: 32, width: 130, background: '#F5F6FA', border: 0, borderRadius: 6, color: '#111827', fontSize: 12 } }}
        />
      ) : null}
      <Button size="xs" variant="light" color="indigo" onClick={onRefresh} loading={loading} styles={{ root: { height: 32, width: 32, padding: 0, background: '#EEF0FF', borderRadius: 6 } }}>
        <IconRefresh size={16} />
      </Button>
    </Group>
  )
}

function SlaProgress({ value, height = 7 }: { value: number | null | undefined; height?: number }) {
  const safe = Math.max(0, Math.min(100, Number(value ?? 0)))
  return (
    <Box style={{ height, borderRadius: 999, background: '#E3E5F0', overflow: 'hidden', minWidth: 160 }}>
      <Box style={{ height: '100%', width: `${safe}%`, borderRadius: 999, background: slaColor(safe) }} />
    </Box>
  )
}

function SlaHeroCard({ title, metric, previous, targetLabelText, description }: { title: string; metric?: SlaMetric; previous?: SlaMetric; targetLabelText: string; description: string }) {
  const value = metric?.actual_percent
  const target = 100
  const lower = Math.max(0, target - Number(value ?? 0))
  const total = Number(metric?.total ?? 0)
  const met = Number(metric?.met ?? 0)
  const breached = Number(metric?.breached ?? 0)
  const pending = Number(metric?.pending ?? 0)
  return (
    <Card withBorder radius="md" p={18} style={{ borderColor: SLA_BORDER, minHeight: 164, boxShadow: 'none' }}>
      <Stack gap={10}>
        <Group justify="space-between" align="flex-start">
          <Text size="sm" fw={700} c="#8190A7">{title}</Text>
          <Text size="sm" fw={700} c="#8190A7">Цель: {targetLabelText}</Text>
        </Group>
        <Group justify="space-between" align="end">
          <Text fw={800} style={{ color: slaColor(value), fontSize: 31, lineHeight: 1 }}>{slaPercent(value)}</Text>
        </Group>
        <Group justify="space-between" gap={10}>
          <Text size="xs" fw={700} c={SLA_RED}>Ниже цели на {slaPercent(lower)}</Text>
          <Text size="xs" fw={700} c={Number(value ?? 0) >= Number(previous?.actual_percent ?? 0) ? SLA_GREEN : SLA_RED}>{slaDeltaText(value, previous?.actual_percent)}</Text>
        </Group>
        <SlaProgress value={value} />
        <Text size="xs" c="#8FA0B6">{formatNumber(met)} из {formatNumber(total)} SLA-метрик выполнены в срок<br />({description})</Text>
        <Text size="xs" c="#8FA0B6">{formatNumber(breached)} нарушено • {formatNumber(pending)} в ожидании</Text>
      </Stack>
    </Card>
  )
}

function SlaTinyCard({ label, value, delta, danger = false }: { label: string; value: string; delta?: string; danger?: boolean }) {
  return (
    <Card withBorder radius="md" p={14} style={{ borderColor: SLA_BORDER, minHeight: 74 }}>
      <Stack gap={8}>
        <Text size="sm" fw={700} c="#8190A7">{label}</Text>
        <Group justify="space-between" align="end">
          <Text fw={800} c={danger ? SLA_RED : '#1E1F24'} size="xl">{value}</Text>
          {delta ? <Text size="xs" c="#8190A7">{delta}</Text> : null}
        </Group>
      </Stack>
    </Card>
  )
}

function SlaSectionCard({ title, subtitle, right, children }: { title: string; subtitle?: string; right?: ReactNode; children: ReactNode }) {
  return (
    <Card withBorder radius="md" p={14} style={{ borderColor: SLA_BORDER, boxShadow: 'none' }}>
      <Stack gap={14}>
        <Group justify="space-between" align="flex-start">
          <Box>
            <Text fw={700} size="lg" lh={1.2}>{title}</Text>
            {subtitle ? <Text size="xs" c="#8FA0B6" mt={2}>{subtitle}</Text> : null}
          </Box>
          {right}
        </Group>
        {children}
      </Stack>
    </Card>
  )
}

function SlaSummaryBand({ items }: { items: Array<{ label: string; value: string; accent?: boolean }> }) {
  return (
    <Box style={{ display: 'grid', gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))`, gap: 0, background: SLA_SOFT, borderRadius: 6, padding: '14px 16px' }}>
      {items.map((item) => (
        <Box key={item.label}>
          <Text size="xs" c="#97A6BA">{item.label}</Text>
          <Text fw={800} mt={6} size="xl" c={item.accent ? SLA_RED : '#1E1F24'}>{item.value}</Text>
        </Box>
      ))}
    </Box>
  )
}

function SlaDynamicsTable({ rows, kind }: { rows: SlaBreakdownRow[]; kind: 'product' | 'channel' | 'queue' | 'agent' }) {
  const visible = rows.slice(0, 7)
  return (
    <Table withRowBorders={false} style={{ tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
      <Table.Thead bg="#EEF1FA">
        <Table.Tr>
          <Table.Th style={{ color: SLA_BLUE, textAlign: 'center' }}>{kind === 'agent' || kind === 'queue' ? 'Очередь' : 'Продукт'}</Table.Th>
          <Table.Th style={{ color: SLA_BLUE, textAlign: 'center' }}>В работе</Table.Th>
          <Table.Th style={{ color: SLA_BLUE, textAlign: 'center', width: 320 }}>SLA/Цель</Table.Th>
          <Table.Th style={{ color: SLA_BLUE, textAlign: 'center' }}>{kind === 'queue' ? 'Медиана ожидания' : kind === 'agent' ? 'Обработано' : 'Отклонения'}</Table.Th>
          <Table.Th style={{ color: SLA_BLUE, textAlign: 'center' }}>{kind === 'agent' ? 'Просрочено/Доля' : 'Доля'}</Table.Th>
          <Table.Th style={{ color: SLA_BLUE, textAlign: 'center' }}>{kind === 'agent' ? 'Доля просрочки' : 'Нарушено'}</Table.Th>
          <Table.Th style={{ color: SLA_BLUE, textAlign: 'center' }}>Динамика нарушений</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {visible.map((row, index) => {
          const actual = row.actual_percent
          const delta = row.breach_delta_percent
          const down = delta != null && delta < 0
          return (
            <Table.Tr key={row.key || `${row.label}-${index}`} bg={index % 2 === 0 ? '#F7F8FD' : '#FFFFFF'}>
              <Table.Td style={{ textAlign: 'center', fontWeight: 700 }}>
                {kind === 'agent' ? (
                  <Group gap={8} wrap="nowrap">
                    <Avatar size={26} radius="xl" color={row.key === '__unassigned__' ? 'red' : 'indigo'} variant="light">{row.key === '__unassigned__' ? '—' : <IconUser size={16} />}</Avatar>
                    <Box ta="left">
                      <Text size="sm" fw={700}>{row.label}</Text>
                      <Text size="xs" c="#69758A">{row.agent_subtitle || 'Агент поддержки'}</Text>
                    </Box>
                  </Group>
                ) : row.label}
              </Table.Td>
              <Table.Td ta="center">{formatNumber(row.in_work ?? row.total)}</Table.Td>
              <Table.Td>
                {kind === 'agent' && row.key === '__unassigned__' ? (
                  <Text ta="center">Не рассчитывается</Text>
                ) : (
                  <Group gap={10} wrap="nowrap">
                    <SlaProgress value={actual} />
                    <Text size="sm" fw={700} c={slaColor(actual)} w={48} ta="right">{slaPercent(actual)}</Text>
                  </Group>
                )}
              </Table.Td>
              <Table.Td ta="center">
                {kind === 'queue' ? `${Math.round(Number(row.median_wait_minutes ?? 0))} мин` : kind === 'agent' ? (row.key === '__unassigned__' ? '—' : slaPercent(row.processed_percent)) : slaPercent(Math.abs(Number(row.deviation_percent ?? 0)))}
              </Table.Td>
              <Table.Td ta="center">
                {kind === 'agent' ? `${formatNumber(row.breached)} / ${slaPercent(row.share_percent)}` : slaPercent(row.share_percent)}
              </Table.Td>
              <Table.Td ta="center" c={SLA_RED}>{kind === 'agent' ? slaPercent(row.share_percent) : formatNumber(row.breached)}</Table.Td>
              <Table.Td ta="center" c={down ? SLA_GREEN : SLA_RED} fw={700}>{delta == null ? '—' : `${down ? '↓' : '↑'} ${slaPercent(Math.abs(delta), 0)}`}</Table.Td>
            </Table.Tr>
          )
        })}
      </Table.Tbody>
    </Table>
  )
}

function SlaDetailTable({ first, resolution }: { first?: SlaMetric; resolution?: SlaMetric }) {
  const rows = [
    { label: 'First Response SLA', data: first },
    { label: 'Resolution SLA', data: resolution },
  ]
  return (
    <Table withRowBorders={false} style={{ tableLayout: 'fixed' }}>
      <Table.Thead bg="#EEF1FA">
        <Table.Tr>
          {['Метрика', 'Факт/Цель', 'Отклонение', 'Структура расчёта', 'В SLA', 'В ожидании', 'Нарушено', 'Всего'].map((head) => <Table.Th key={head} ta="center" c={SLA_BLUE}>{head}</Table.Th>)}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((row, index) => {
          const actual = row.data?.actual_percent
          const deviation = actual == null ? null : actual - 100
          return (
            <Table.Tr key={row.label} bg={index % 2 === 0 ? '#F7F8FD' : '#FFFFFF'}>
              <Table.Td fw={700}>{row.label}</Table.Td>
              <Table.Td ta="center">{slaPercent(actual)} / 100%</Table.Td>
              <Table.Td ta="center" c={SLA_RED}>{deviation == null ? '—' : slaPercent(deviation)}</Table.Td>
              <Table.Td><SlaProgress value={actual} /></Table.Td>
              <Table.Td ta="center">{formatNumber(row.data?.met)}</Table.Td>
              <Table.Td ta="center">{formatNumber(row.data?.pending)}</Table.Td>
              <Table.Td ta="center" c={SLA_RED}>{formatNumber(row.data?.breached)}</Table.Td>
              <Table.Td ta="center">{formatNumber(row.data?.total)}</Table.Td>
            </Table.Tr>
          )
        })}
      </Table.Tbody>
    </Table>
  )
}

function OperatorTimeCell({ current, previous }: { current: number | null | undefined; previous: number | null | undefined }) {
  const delta = compactChangeLine(current, previous, true)
  const deltaColor = delta?.tone === 'green' ? SLA_GREEN : delta?.tone === 'red' ? SLA_RED : '#8190A7'
  return (
    <Stack gap={3} align="center">
      <Text size="sm" fw={700}>{formatDurationClock(current)}</Text>
      {delta ? <Text size="xs" fw={700} c={deltaColor}>{delta.text}</Text> : null}
    </Stack>
  )
}

export function AnalyticsNewPage() {
  const { user } = useAuth()
  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  const [period, setPeriod] = useState<DashboardPeriodKey>('7')
  const [projectIds, setProjectIds] = useState<string[]>([])
  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [customFrom, setCustomFrom] = useState(() => toLocalDatetimeValue(new Date(Date.now() - 24 * 60 * 60 * 1000)))
  const [customTo, setCustomTo] = useState(() => toLocalDatetimeValue(new Date()))
  // «Свой период» loads only after «Показать» — selecting it must not hit the API.
  const [customApplied, setCustomApplied] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [dashboardLoading, setDashboardLoading] = useState(false)
  const [operatorsLoading, setOperatorsLoading] = useState(false)
  const [operatorKtuLoading, setOperatorKtuLoading] = useState(false)
  const [operatorKtuError, setOperatorKtuError] = useState<string | null>(null)
  const [aiOpsLoading, setAiOpsLoading] = useState(false)
  const [dashboardError, setDashboardError] = useState<string | null>(null)
  const [slaSummaryError, setSlaSummaryError] = useState<string | null>(null)
  const [operatorsError, setOperatorsError] = useState<string | null>(null)
  const [aiOpsError, setAiOpsError] = useState<string | null>(null)
  const [stats, setStats] = useState<AnalyticsDashboardStats | null>(null)
  const [prevStats, setPrevStats] = useState<AnalyticsDashboardStats | null>(null)
  const [slaSummaryStats, setSlaSummaryStats] = useState<AnalyticsDashboardStats | null>(null)
  const [prevSlaSummaryStats, setPrevSlaSummaryStats] = useState<AnalyticsDashboardStats | null>(null)
  const [slaSummaryLoading, setSlaSummaryLoading] = useState(false)
  const [hourlyStats, setHourlyStats] = useState<AnalyticsDashboardStats | null>(null)
  const [prevHourlyStats, setPrevHourlyStats] = useState<AnalyticsDashboardStats | null>(null)
  const [aiOps, setAiOps] = useState<AiOperationsAnalyticsResponse | null>(null)
  const [prevAiOps, setPrevAiOps] = useState<AiOperationsAnalyticsResponse | null>(null)
  const [operators, setOperators] = useState<OperatorOverviewRow[]>([])
  const [prevOperators, setPrevOperators] = useState<OperatorOverviewRow[]>([])
  const [operatorTimes, setOperatorTimes] = useState<OperatorOverviewRow[]>([])
  const [prevOperatorTimes, setPrevOperatorTimes] = useState<OperatorOverviewRow[]>([])
  const [operatorTimesLoading, setOperatorTimesLoading] = useState(false)
  const [operatorTimesError, setOperatorTimesError] = useState<string | null>(null)
  const [ktuOperatorsDetailed, setKtuOperatorsDetailed] = useState<OperatorKtuRow[]>([])
  const [prevKtuOperatorsDetailed, setPrevKtuOperatorsDetailed] = useState<OperatorKtuRow[]>([])
  const [withoutAIProducts, setWithoutAIProducts] = useState(true)
  const [withoutAIHourly, setWithoutAIHourly] = useState(true)
  const [hourlyBasis, setHourlyBasis] = useState<HourlyBasis>('closed')
  const [hourlyLoading, setHourlyLoading] = useState(false)
  const [operatorChartIds, setOperatorChartIds] = useState<string[] | null>(null)
  const [productChartIds, setProductChartIds] = useState<string[] | null>(null)
  const [operatorKtuChartIds, setOperatorKtuChartIds] = useState<string[] | null>(null)
  const [projectTimeChartIds, setProjectTimeChartIds] = useState<string[] | null>(null)
  const [analyticsTab, setAnalyticsTab] = useState<'overview' | 'sla' | 'ai-ops' | 'operator-times'>('overview')
  const [slaChartMetric, setSlaChartMetric] = useState<'actual' | 'resolution' | 'first_response'>('actual')
  const [slaFilters, setSlaFilters] = useState<Record<SlaFilterKey, SlaLocalFilter>>({
    timeline: { period: '7', projectIds: [] },
    product: { period: '7', projectIds: [] },
    channel: { period: '7', projectIds: [] },
    queue: { period: '7', projectIds: [] },
    agent: { period: '7', projectIds: [] },
  })
  const [slaSectionStats, setSlaSectionStats] = useState<Record<SlaFilterKey, AnalyticsDashboardStats | null>>({
    timeline: null,
    product: null,
    channel: null,
    queue: null,
    agent: null,
  })
  const [slaSectionLoading, setSlaSectionLoading] = useState<Record<SlaFilterKey, boolean>>({
    timeline: false,
    product: false,
    channel: false,
    queue: false,
    agent: false,
  })
  const [operatorSelectionWarning, setOperatorSelectionWarning] = useState<string | null>(null)
  const [productSelectionWarning, setProductSelectionWarning] = useState<string | null>(null)
  const [operatorKtuSelectionWarning, setOperatorKtuSelectionWarning] = useState<string | null>(null)
  const [projectTimeSelectionWarning, setProjectTimeSelectionWarning] = useState<string | null>(null)
  const [operatorModal, setOperatorModal] = useState<{ operatorId: string; block: OperatorTicketsBlock; label: string } | null>(null)
  const [productModal, setProductModal] = useState<ProjectRow | null>(null)
  const [aiReasonModal, setAiReasonModal] = useState<{ reason: AiReasonKey; label: string } | null>(null)
  const [productTooltip, setProductTooltip] = useState<{ visible: boolean; x: number; y: number; chartWidth: number; chartHeight: number; viewportX: number; viewportY: number; viewportWidth: number; viewportHeight: number; data: ProductTooltipData | null }>({
    visible: false,
    x: 0,
    y: 0,
    chartWidth: 0,
    chartHeight: 0,
    viewportX: 0,
    viewportY: 0,
    viewportWidth: 0,
    viewportHeight: 0,
    data: null,
  })
  const [chartRowsTooltip, setChartRowsTooltip] = useState<{ visible: boolean; viewportX: number; viewportY: number; viewportWidth: number; viewportHeight: number; height: number; data: ChartRowsTooltipData | null }>({
    visible: false,
    viewportX: 0,
    viewportY: 0,
    viewportWidth: 0,
    viewportHeight: 0,
    height: 0,
    data: null,
  })
  const operatorChartRef = useRef<ChartJS<'bar'> | null>(null)
  const productChartRef = useRef<ChartJS<'bar'> | null>(null)
  const operatorKtuChartRef = useRef<ChartJS<'bar'> | null>(null)
  const projectTimeChartRef = useRef<ChartJS<'bar'> | null>(null)
  const aiProjectChartRef = useRef<ChartJS<'bar'> | null>(null)
  const overviewProjectSourceChartRef = useRef<ChartJS<'bar'> | null>(null)
  const aiProjectSourceChartRef = useRef<ChartJS<'bar'> | null>(null)
  const dashboardRequestRef = useRef(0)
  const slaSummaryRequestRef = useRef(0)
  const slaSectionRequestRef = useRef<Record<SlaFilterKey, number>>({
    timeline: 0,
    product: 0,
    channel: 0,
    queue: 0,
    agent: 0,
  })
  const hourlyRequestRef = useRef(0)
  const operatorsRequestRef = useRef(0)
  const aiOpsRequestRef = useRef(0)
  const operatorTimesRequestRef = useRef(0)

  useEffect(() => { void loadMyPermissions(user?.role).then(setPerm) }, [user?.role])

  const permissions = perm ?? defaultPermissionsForRole(user?.role)
  const canAnalytics = hasPermission(permissions, 'access_analytics')

  useEffect(() => {
    if (!canAnalytics) return
    void api.projects().then((list) => setProjects([...list].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)))).catch(() => setProjects([]))
  }, [canAnalytics])

  const projectIdCsv = useMemo(() => projectIds.join(','), [projectIds])
  const customPeriodPending = period === 'custom_datetime' && !customApplied
  // Drop in-flight work and busy flags together — stale responses won't clear loading themselves.
  useEffect(() => {
    if (!customPeriodPending) return
    dashboardRequestRef.current += 1
    slaSummaryRequestRef.current += 1
    hourlyRequestRef.current += 1
    operatorsRequestRef.current += 1
    aiOpsRequestRef.current += 1
    operatorTimesRequestRef.current += 1
    slaSectionRequestRef.current = {
      timeline: (slaSectionRequestRef.current.timeline || 0) + 1,
      product: (slaSectionRequestRef.current.product || 0) + 1,
      channel: (slaSectionRequestRef.current.channel || 0) + 1,
      queue: (slaSectionRequestRef.current.queue || 0) + 1,
      agent: (slaSectionRequestRef.current.agent || 0) + 1,
    }
    setDashboardLoading(false)
    setSlaSummaryLoading(false)
    setHourlyLoading(false)
    setOperatorsLoading(false)
    setOperatorKtuLoading(false)
    setAiOpsLoading(false)
    setOperatorTimesLoading(false)
    setSlaSectionLoading({
      timeline: false,
      product: false,
      channel: false,
      queue: false,
      agent: false,
    })
    // Main summary follows the pending main period — clear stale hero figures.
    setSlaSummaryStats(null)
    setPrevSlaSummaryStats(null)
  }, [customPeriodPending])
  const syncSlaFiltersFromMain = useCallback((nextPeriod: DashboardPeriodKey, nextProjectIds: string[]) => {
    const nextFilter = { period: nextPeriod, projectIds: nextProjectIds }
    setSlaFilters({
      timeline: nextFilter,
      product: nextFilter,
      channel: nextFilter,
      queue: nextFilter,
      agent: nextFilter,
    })
  }, [])
  const handlePeriodChange = useCallback((next: DashboardPeriodKey) => {
    setPeriod(next)
    setCustomApplied(next !== 'custom_datetime')
    // Main filters own all section strips; section-local custom must not go through here.
    syncSlaFiltersFromMain(next, projectIds)
  }, [projectIds, syncSlaFiltersFromMain])
  const handleProjectIdsChange = useCallback((nextProjectIds: string[]) => {
    // Pending custom keeps previously loaded cards; do not change their project mid-draft.
    if (period === 'custom_datetime' && !customApplied) return
    setProjectIds(nextProjectIds)
    syncSlaFiltersFromMain(period, nextProjectIds)
  }, [period, customApplied, syncSlaFiltersFromMain])
  const handleCustomApply = useCallback(() => setCustomApplied(true), [])
  const periodParams = useMemo(() => {
    let customFromIso: string | null = null
    let customToIso: string | null = null
    if (period === 'custom_datetime' && customApplied && customFrom && customTo) {
      const from = new Date(customFrom)
      const to = new Date(customTo)
      if (!Number.isNaN(from.getTime())) customFromIso = from.toISOString()
      if (!Number.isNaN(to.getTime())) customToIso = to.toISOString()
    }
    return resolveDashboardPeriodParams(buildDashboardPeriodParams(period, { projectId: projectIdCsv || undefined, customFromIso, customToIso, excludeBurst: false }))
  }, [period, projectIdCsv, customFrom, customTo, customApplied, refreshKey])

  const baseQuery = useMemo(() => periodParamsToAnalyticsQuery(periodParams), [periodParams])
  const operatorsQuery = useMemo(
    () => ({ ...baseQuery, exclude_closed_emergency: true }),
    [baseQuery],
  )
  // Per-dataset snapshots: advance only when that dataset's load accepts rows.
  const [productDrilldownPeriodParams, setProductDrilldownPeriodParams] = useState(periodParams)
  const [productDrilldownWithoutAI, setProductDrilldownWithoutAI] = useState(withoutAIProducts)
  const [operatorDrilldownQuery, setOperatorDrilldownQuery] = useState(operatorsQuery)
  const [aiOpsDrilldownBaseQuery, setAiOpsDrilldownBaseQuery] = useState(baseQuery)
  const dashboardQuery = useMemo(
    () => ({ ...baseQuery, analytics_scope: 'overview', exclude_closed_emergency: false, ...(withoutAIProducts ? { without_ai_assistant: true } : {}) }),
    [baseQuery, withoutAIProducts],
  )
  // The incoming flow uses a lightweight scope; the closed one shares the overview request.
  const hourlyScope = hourlyBasis === 'created' ? 'hourly_created' : 'overview'
  const hourlyDashboardQuery = useMemo(
    () => ({ ...baseQuery, analytics_scope: hourlyScope, exclude_closed_emergency: false, ...(withoutAIHourly ? { without_ai_assistant: true } : {}) }),
    [baseQuery, hourlyScope, withoutAIHourly],
  )
  const prevParams = useMemo(() => getPreviousPeriodParams(periodParams), [periodParams])
  const prevQuery = useMemo(() => {
    if (!prevParams) return null
    const query = periodParamsToAnalyticsQuery(prevParams)
    return { ...query, analytics_scope: 'overview', exclude_closed_emergency: false, ...(withoutAIProducts ? { without_ai_assistant: true } : {}) }
  }, [prevParams, withoutAIProducts])
  const prevHourlyQuery = useMemo(() => {
    if (!prevParams) return null
    const query = periodParamsToAnalyticsQuery(prevParams)
    return { ...query, analytics_scope: hourlyScope, exclude_closed_emergency: false, ...(withoutAIHourly ? { without_ai_assistant: true } : {}) }
  }, [prevParams, hourlyScope, withoutAIHourly])
  const sameHourlyAsDashboard = useMemo(
    () => JSON.stringify(hourlyDashboardQuery) === JSON.stringify(dashboardQuery) && JSON.stringify(prevHourlyQuery) === JSON.stringify(prevQuery),
    [hourlyDashboardQuery, dashboardQuery, prevHourlyQuery, prevQuery],
  )
  const prevBaseQuery = useMemo(() => prevParams ? periodParamsToAnalyticsQuery(prevParams) : null, [prevParams])
  const prevOperatorsQuery = useMemo(
    () => prevBaseQuery ? ({ ...prevBaseQuery, exclude_closed_emergency: true }) : null,
    [prevBaseQuery],
  )

  const loadDashboard = useCallback(async () => {
    if (!canAnalytics) return
    if (analyticsTab !== 'overview') {
      dashboardRequestRef.current += 1
      setDashboardLoading(false)
      return
    }
    if (customPeriodPending) return
    if (period === 'custom_datetime' && (!customFrom || !customTo)) return
    const requestId = dashboardRequestRef.current + 1
    dashboardRequestRef.current = requestId
    const acceptedPeriodParams = periodParams
    const acceptedWithoutAI = withoutAIProducts
    setDashboardLoading(true)
    setDashboardError(null)
    setPrevStats(null)
    try {
      const previousDashboardPromise = prevQuery
        ? api.analyticsDashboard(prevQuery).catch(() => null)
        : Promise.resolve(null)
      const currentDashboard = await api.analyticsDashboard(dashboardQuery)
      if (dashboardRequestRef.current !== requestId) return
      setStats(currentDashboard)
      setProductDrilldownPeriodParams(acceptedPeriodParams)
      setProductDrilldownWithoutAI(acceptedWithoutAI)
      setDashboardLoading(false)

      if (prevQuery) {
        const previousDashboard = await previousDashboardPromise
        if (dashboardRequestRef.current !== requestId) return
        setPrevStats(previousDashboard || null)
      } else {
        setPrevStats(null)
      }
    } catch (err) {
      if (dashboardRequestRef.current !== requestId) return
      setDashboardError(err instanceof ApiError ? err.message : 'Не удалось загрузить аналитику по продуктам')
      setStats(null)
      setPrevStats(null)
      setDashboardLoading(false)
    }
  }, [canAnalytics, analyticsTab, period, customFrom, customTo, customPeriodPending, dashboardQuery, prevQuery, periodParams, withoutAIProducts])

  const loadSlaSummary = useCallback(async () => {
    if (!canAnalytics) return
    if (analyticsTab !== 'sla') {
      slaSummaryRequestRef.current += 1
      setSlaSummaryLoading(false)
      return
    }
    if (customPeriodPending) return
    if (period === 'custom_datetime' && (!customFrom || !customTo)) return
    const requestId = slaSummaryRequestRef.current + 1
    slaSummaryRequestRef.current = requestId
    setSlaSummaryLoading(true)
    setSlaSummaryError(null)
    setPrevSlaSummaryStats(null)
    try {
      const query = { ...baseQuery, analytics_scope: 'sla_summary', without_ai_assistant: true }
      const previousPromise = prevBaseQuery
        ? api.analyticsDashboard({ ...prevBaseQuery, analytics_scope: 'sla_summary', without_ai_assistant: true }).catch(() => null)
        : Promise.resolve(null)
      const current = await api.analyticsDashboard(query)
      if (slaSummaryRequestRef.current !== requestId) return
      setSlaSummaryStats(current)
      setSlaSummaryLoading(false)
      const previous = await previousPromise
      if (slaSummaryRequestRef.current !== requestId) return
      setPrevSlaSummaryStats(previous)
    } catch (err) {
      if (slaSummaryRequestRef.current !== requestId) return
      setSlaSummaryError(err instanceof ApiError ? err.message : 'Не удалось загрузить SLA')
      setSlaSummaryStats(null)
      setPrevSlaSummaryStats(null)
      setSlaSummaryLoading(false)
    }
  }, [canAnalytics, analyticsTab, period, customFrom, customTo, customPeriodPending, baseQuery, prevBaseQuery])

  const loadHourlyDashboard = useCallback(async () => {
    if (!canAnalytics) return
    if (analyticsTab !== 'overview') {
      hourlyRequestRef.current += 1
      setHourlyLoading(false)
      return
    }
    if (sameHourlyAsDashboard) {
      hourlyRequestRef.current += 1
      setHourlyLoading(false)
      return
    }
    if (customPeriodPending) return
    if (period === 'custom_datetime' && (!customFrom || !customTo)) return
    const requestId = hourlyRequestRef.current + 1
    hourlyRequestRef.current = requestId
    setHourlyLoading(true)
    setPrevHourlyStats(null)
    try {
      const previousDashboardPromise = prevHourlyQuery
        ? api.analyticsDashboard(prevHourlyQuery).catch(() => null)
        : Promise.resolve(null)
      const currentDashboard = await api.analyticsDashboard(hourlyDashboardQuery)
      if (hourlyRequestRef.current !== requestId) return
      setHourlyStats(currentDashboard)
      setHourlyLoading(false)
      if (prevHourlyQuery) {
        const previousDashboard = await previousDashboardPromise
        if (hourlyRequestRef.current === requestId) setPrevHourlyStats(previousDashboard)
      } else {
        setPrevHourlyStats(null)
      }
    } catch {
      if (hourlyRequestRef.current !== requestId) return
      setHourlyStats(null)
      setPrevHourlyStats(null)
      setHourlyLoading(false)
    }
  }, [canAnalytics, analyticsTab, sameHourlyAsDashboard, period, customFrom, customTo, customPeriodPending, hourlyDashboardQuery, prevHourlyQuery])

  const loadOperators = useCallback(async () => {
    if (!canAnalytics) return
    if (analyticsTab !== 'overview') {
      operatorsRequestRef.current += 1
      setOperatorsLoading(false)
      setOperatorKtuLoading(false)
      return
    }
    if (customPeriodPending) return
    if (period === 'custom_datetime' && (!customFrom || !customTo)) return
    const requestId = operatorsRequestRef.current + 1
    operatorsRequestRef.current = requestId
    // Match the chart query (incl. exclude_closed_emergency); do not share product snapshot.
    const acceptedOperatorsQuery = operatorsQuery
    setOperatorsLoading(true)
    setOperatorKtuLoading(true)
    setOperatorKtuError(null)
    setOperatorsError(null)
    setPrevOperators([])
    setKtuOperatorsDetailed([])
    setPrevKtuOperatorsDetailed([])
    try {
      const previousOperatorsPromise = prevOperatorsQuery
        ? api.analyticsOperatorsOverview({ ...prevOperatorsQuery, skip_timing_metrics: true }).catch(() => [])
        : Promise.resolve([])
      const currentOperators = await api.analyticsOperatorsOverview({ ...operatorsQuery, skip_timing_metrics: true })
      if (operatorsRequestRef.current !== requestId) return
      setOperators(Array.isArray(currentOperators) ? currentOperators : [])
      setOperatorDrilldownQuery(acceptedOperatorsQuery)
      setOperatorsLoading(false)

      if (prevOperatorsQuery && operatorsRequestRef.current === requestId) {
        previousOperatorsPromise.then((previousOperators) => {
          if (operatorsRequestRef.current === requestId) {
            setPrevOperators(Array.isArray(previousOperators) ? previousOperators : [])
          }
        })
      }

    } catch (err) {
      if (operatorsRequestRef.current !== requestId) return
      setOperatorsError(err instanceof ApiError ? err.message : 'Не удалось загрузить аналитику по операторам')
      setOperators([])
      setPrevOperators([])
      setOperatorsLoading(false)
    }

    try {
      const previousDetailedPromise = prevBaseQuery
        ? api.analyticsOperatorsKtu(prevBaseQuery).catch(() => null)
        : Promise.resolve(null)
      const currentDetailed = await api.analyticsOperatorsKtu(baseQuery)
      if (operatorsRequestRef.current !== requestId) return
      setKtuOperatorsDetailed(Array.isArray(currentDetailed?.operators) ? currentDetailed.operators : [])
      setOperatorKtuLoading(false)

      if (prevBaseQuery && operatorsRequestRef.current === requestId) {
        previousDetailedPromise.then((previousDetailed) => {
          if (operatorsRequestRef.current === requestId) {
            setPrevKtuOperatorsDetailed(Array.isArray(previousDetailed?.operators) ? previousDetailed.operators : [])
          }
        })
      }
    } catch (err) {
      if (operatorsRequestRef.current !== requestId) return
      setOperatorKtuError(err instanceof ApiError ? err.message : 'Не удалось загрузить КТУ операторов')
      setKtuOperatorsDetailed([])
      setPrevKtuOperatorsDetailed([])
      setOperatorKtuLoading(false)
    }
  }, [canAnalytics, analyticsTab, period, customFrom, customTo, customPeriodPending, operatorsQuery, prevOperatorsQuery, baseQuery, prevBaseQuery])

  const loadAiOps = useCallback(async () => {
    if (!canAnalytics) return
    if (analyticsTab !== 'ai-ops') {
      aiOpsRequestRef.current += 1
      setAiOpsLoading(false)
      return
    }
    if (customPeriodPending) return
    if (period === 'custom_datetime' && (!customFrom || !customTo)) return
    const requestId = aiOpsRequestRef.current + 1
    aiOpsRequestRef.current = requestId
    const acceptedBaseQuery = baseQuery
    setAiOpsLoading(true)
    setAiOpsError(null)
    setPrevAiOps(null)
    try {
      const previousAiOpsPromise = prevBaseQuery
        ? api.analyticsAiOperations(prevBaseQuery).catch(() => null)
        : Promise.resolve(null)
      const currentAiOps = await api.analyticsAiOperations(baseQuery)
      if (aiOpsRequestRef.current !== requestId) return
      setAiOps(currentAiOps)
      setAiOpsDrilldownBaseQuery(acceptedBaseQuery)
      setAiOpsLoading(false)
      if (prevBaseQuery) {
        const previousAiOps = await previousAiOpsPromise
        if (aiOpsRequestRef.current === requestId) setPrevAiOps(previousAiOps)
      } else {
        setPrevAiOps(null)
      }
    } catch (err) {
      if (aiOpsRequestRef.current !== requestId) return
      setAiOpsError(err instanceof ApiError ? err.message : 'Не удалось загрузить аналитику Мозгобота')
      setAiOps(null)
      setPrevAiOps(null)
      setAiOpsLoading(false)
    }
  }, [analyticsTab, baseQuery, canAnalytics, customFrom, customTo, customPeriodPending, period, prevBaseQuery])

  const loadOperatorTimes = useCallback(async () => {
    if (!canAnalytics) return
    if (analyticsTab !== 'operator-times') {
      operatorTimesRequestRef.current += 1
      setOperatorTimesLoading(false)
      return
    }
    if (customPeriodPending) return
    if (period === 'custom_datetime' && (!customFrom || !customTo)) return
    const requestId = operatorTimesRequestRef.current + 1
    operatorTimesRequestRef.current = requestId
    setOperatorTimesLoading(true)
    setOperatorTimesError(null)
    setPrevOperatorTimes([])
    try {
      const previousOperatorTimesPromise = prevOperatorsQuery
        ? api.analyticsOperatorsOverview(prevOperatorsQuery).catch(() => [])
        : Promise.resolve([])
      const currentOperatorTimes = await api.analyticsOperatorsOverview(operatorsQuery)
      if (operatorTimesRequestRef.current !== requestId) return
      setOperatorTimes(Array.isArray(currentOperatorTimes) ? currentOperatorTimes : [])
      setOperatorTimesLoading(false)

      if (prevOperatorsQuery && operatorTimesRequestRef.current === requestId) {
        previousOperatorTimesPromise.then((previousOperatorTimes) => {
          if (operatorTimesRequestRef.current === requestId) {
            setPrevOperatorTimes(Array.isArray(previousOperatorTimes) ? previousOperatorTimes : [])
          }
        })
      }
    } catch (err) {
      if (operatorTimesRequestRef.current !== requestId) return
      setOperatorTimesError(err instanceof ApiError ? err.message : 'Не удалось загрузить время операторов')
      setOperatorTimes([])
      setPrevOperatorTimes([])
      setOperatorTimesLoading(false)
    }
  }, [canAnalytics, analyticsTab, period, customFrom, customTo, customPeriodPending, operatorsQuery, prevOperatorsQuery])

  useEffect(() => { void loadDashboard() }, [loadDashboard, refreshKey])
  useEffect(() => { void loadSlaSummary() }, [loadSlaSummary, refreshKey])
  useEffect(() => { void loadHourlyDashboard() }, [loadHourlyDashboard, refreshKey])
  useEffect(() => { void loadOperators() }, [loadOperators, refreshKey])
  useEffect(() => { void loadAiOps() }, [loadAiOps, refreshKey])
  useEffect(() => { void loadOperatorTimes() }, [loadOperatorTimes, refreshKey])
  const onRefresh = useCallback(() => setRefreshKey((key) => key + 1), [])
  const anySlaSectionLoading = Object.values(slaSectionLoading).some(Boolean)
  const filtersLoading =
    analyticsTab === 'overview'
      ? dashboardLoading || operatorsLoading || operatorKtuLoading || (!sameHourlyAsDashboard && hourlyLoading)
      : analyticsTab === 'sla'
        ? slaSummaryLoading || anySlaSectionLoading
        : analyticsTab === 'ai-ops'
          ? aiOpsLoading
          : operatorTimesLoading
  const loading = filtersLoading

  const updateSlaFilter = useCallback((key: SlaFilterKey, patch: Partial<SlaLocalFilter>) => {
    setSlaFilters((prev) => ({
      ...prev,
      [key]: { ...prev[key], ...patch },
    }))
  }, [])

  const slaSectionScope = (key: SlaFilterKey) => {
    if (key === 'timeline') return 'sla_timeline'
    if (key === 'product') return 'sla_product'
    if (key === 'channel') return 'sla_channel'
    if (key === 'queue') return 'sla_queue'
    return 'sla_agent'
  }

  const buildSlaSectionQuery = useCallback((key: SlaFilterKey, filter: SlaLocalFilter) => {
    let customFromIso: string | null = null
    let customToIso: string | null = null
    if (filter.period === 'custom_datetime' && customApplied && customFrom && customTo) {
      const from = new Date(customFrom)
      const to = new Date(customTo)
      if (!Number.isNaN(from.getTime())) customFromIso = from.toISOString()
      if (!Number.isNaN(to.getTime())) customToIso = to.toISOString()
    }
    const params = resolveDashboardPeriodParams(buildDashboardPeriodParams(filter.period, {
      projectId: filter.projectIds.join(',') || undefined,
      customFromIso,
      customToIso,
      excludeBurst: false,
    }))
    return { ...periodParamsToAnalyticsQuery(params), analytics_scope: slaSectionScope(key), without_ai_assistant: true }
  }, [customApplied, customFrom, customTo, refreshKey])

  const loadSlaSection = useCallback(async (key: SlaFilterKey, filter: SlaLocalFilter) => {
    if (!canAnalytics) return
    // Main custom pending: keep non-custom siblings, clear strips already on custom.
    if (customPeriodPending) {
      slaSectionRequestRef.current[key] = (slaSectionRequestRef.current[key] || 0) + 1
      if (filter.period === 'custom_datetime') {
        setSlaSectionStats((prev) => (prev[key] == null ? prev : { ...prev, [key]: null }))
      }
      setSlaSectionLoading((prev) => (prev[key] ? { ...prev, [key]: false } : prev))
      return
    }
    // Section strips have no date/apply UI — custom waits on the main «Показать».
    if (filter.period === 'custom_datetime' && !customApplied) {
      slaSectionRequestRef.current[key] = (slaSectionRequestRef.current[key] || 0) + 1
      setSlaSectionStats((prev) => (prev[key] == null ? prev : { ...prev, [key]: null }))
      setSlaSectionLoading((prev) => (prev[key] ? { ...prev, [key]: false } : prev))
      return
    }
    if (filter.period === 'custom_datetime' && (!customFrom || !customTo)) return
    const requestId = (slaSectionRequestRef.current[key] || 0) + 1
    slaSectionRequestRef.current[key] = requestId
    setSlaSectionLoading((prev) => ({ ...prev, [key]: true }))
    try {
      const data = await api.analyticsDashboard(buildSlaSectionQuery(key, filter))
      if (slaSectionRequestRef.current[key] !== requestId) return
      setSlaSectionStats((prev) => ({ ...prev, [key]: data }))
    } catch {
      if (slaSectionRequestRef.current[key] !== requestId) return
      setSlaSectionStats((prev) => ({ ...prev, [key]: null }))
    } finally {
      if (slaSectionRequestRef.current[key] === requestId) {
        setSlaSectionLoading((prev) => ({ ...prev, [key]: false }))
      }
    }
  }, [buildSlaSectionQuery, canAnalytics, customApplied, customFrom, customTo, customPeriodPending])

  useEffect(() => { if (analyticsTab === 'sla') void loadSlaSection('timeline', slaFilters.timeline) }, [analyticsTab, loadSlaSection, refreshKey, slaFilters.timeline])
  useEffect(() => { if (analyticsTab === 'sla') void loadSlaSection('product', slaFilters.product) }, [analyticsTab, loadSlaSection, refreshKey, slaFilters.product])
  useEffect(() => { if (analyticsTab === 'sla') void loadSlaSection('channel', slaFilters.channel) }, [analyticsTab, loadSlaSection, refreshKey, slaFilters.channel])
  useEffect(() => { if (analyticsTab === 'sla') void loadSlaSection('queue', slaFilters.queue) }, [analyticsTab, loadSlaSection, refreshKey, slaFilters.queue])
  useEffect(() => { if (analyticsTab === 'sla') void loadSlaSection('agent', slaFilters.agent) }, [analyticsTab, loadSlaSection, refreshKey, slaFilters.agent])

  const visibleOperators = useMemo(() => [...operators].sort((a, b) => (Number(b.closed_tickets) || 0) - (Number(a.closed_tickets) || 0)), [operators])
  const visiblePrevOperators = useMemo(() => [...prevOperators].sort((a, b) => (Number(b.closed_tickets) || 0) - (Number(a.closed_tickets) || 0)), [prevOperators])
  const chartVisibleOperators = useMemo(() => visibleOperators, [visibleOperators])
  const defaultOperatorChartIds = useMemo(
    () => chartVisibleOperators.filter((op) => !isAIOperator(op)).slice(0, 10).map(operatorKey),
    [chartVisibleOperators],
  )
  const activeOperatorChartIds = operatorChartIds ?? defaultOperatorChartIds
  const topOperators = useMemo(() => {
    const selected = new Set(activeOperatorChartIds)
    return chartVisibleOperators.filter((op) => selected.has(operatorKey(op))).slice(0, 10)
  }, [chartVisibleOperators, activeOperatorChartIds])
  const ktuOperators = useMemo(
    () => ktuOperatorsDetailed.filter((op) => op.ktu != null && Number.isFinite(Number(op.ktu))),
    [ktuOperatorsDetailed],
  )
  const defaultOperatorKtuChartIds = useMemo(
    () => ktuOperators.filter((op) => !isAIOperator(op)).slice(0, 10).map(operatorKey),
    [ktuOperators],
  )
  const activeOperatorKtuChartIds = operatorKtuChartIds ?? defaultOperatorKtuChartIds
  const topOperatorKtu = useMemo(() => {
    const selected = new Set(activeOperatorKtuChartIds)
    return ktuOperators.filter((op) => selected.has(operatorKey(op))).slice(0, 10)
  }, [ktuOperators, activeOperatorKtuChartIds])

  const productRows = useMemo(() => {
    const rows = (stats?.by_project ?? []).filter((row) => (row.total ?? 0) > 0 || (row.closed ?? 0) > 0 || (row.resolved ?? 0) > 0 || row.avg_first_response_minutes != null || row.avg_close_minutes != null)
    if (rows.length === 0 && projectIds.length === 1 && (stats?.total_tickets ?? 0) > 0) {
      const project = projects.find((item) => item.id === projectIds[0])
      return [{ project_id: projectIds[0], project_name: project?.name, total: stats?.total_tickets ?? 0 }]
    }
    return [...rows].sort((a, b) => (Number(b.total) || 0) - (Number(a.total) || 0))
  }, [stats?.by_project, stats?.total_tickets, projectIds, projects])
  const defaultProductChartIds = useMemo(() => productRows.slice(0, 10).map(productKey), [productRows])
  const activeProductChartIds = productChartIds ?? defaultProductChartIds
  const topProducts = useMemo(() => {
    const selected = new Set(activeProductChartIds)
    return productRows.filter((row) => selected.has(productKey(row))).slice(0, 10)
  }, [productRows, activeProductChartIds])
  const projectTimeRows = useMemo(
    () => productRows.filter((row) => row.avg_first_response_minutes != null || row.avg_close_minutes != null),
    [productRows],
  )
  const defaultProjectTimeChartIds = useMemo(() => projectTimeRows.slice(0, 10).map(productKey), [projectTimeRows])
  const activeProjectTimeChartIds = projectTimeChartIds ?? defaultProjectTimeChartIds
  const topProjectTime = useMemo(() => {
    const selected = new Set(activeProjectTimeChartIds)
    return projectTimeRows.filter((row) => selected.has(productKey(row))).slice(0, 10)
  }, [projectTimeRows, activeProjectTimeChartIds])

  useEffect(() => {
    setOperatorChartIds(null)
    setOperatorSelectionWarning(null)
    setOperatorKtuChartIds(null)
    setOperatorKtuSelectionWarning(null)
  }, [period, projectIdCsv, customFrom, customTo])

  useEffect(() => {
    setProductChartIds(null)
    setProductSelectionWarning(null)
    setProjectTimeChartIds(null)
    setProjectTimeSelectionWarning(null)
  }, [period, projectIdCsv, customFrom, customTo, withoutAIProducts])

  const toggleOperatorInChart = useCallback((id: string) => {
    setOperatorChartIds((currentIds) => {
      const current = currentIds ?? defaultOperatorChartIds
      if (current.includes(id)) {
        setOperatorSelectionWarning(null)
        return current.filter((item) => item !== id)
      }
      if (current.length >= 10) {
        setOperatorSelectionWarning('На графике можно одновременно показать максимум 10 операторов. Сначала скройте кого-то из выбранных.')
        return current
      }
      setOperatorSelectionWarning(null)
      return [...current, id]
    })
  }, [defaultOperatorChartIds])

  const toggleProductInChart = useCallback((id: string) => {
    setProductChartIds((currentIds) => {
      const current = currentIds ?? defaultProductChartIds
      if (current.includes(id)) {
        setProductSelectionWarning(null)
        return current.filter((item) => item !== id)
      }
      if (current.length >= 10) {
        setProductSelectionWarning('На графике можно одновременно показать максимум 10 продуктов. Сначала скройте какой-то выбранный продукт.')
        return current
      }
      setProductSelectionWarning(null)
      return [...current, id]
    })
  }, [defaultProductChartIds])

  const toggleOperatorKtuInChart = useCallback((id: string) => {
    setOperatorKtuChartIds((currentIds) => {
      const current = currentIds ?? defaultOperatorKtuChartIds
      if (current.includes(id)) {
        setOperatorKtuSelectionWarning(null)
        return current.filter((item) => item !== id)
      }
      if (current.length >= 10) {
        setOperatorKtuSelectionWarning('На графике КТУ можно одновременно показать максимум 10 операторов.')
        return current
      }
      setOperatorKtuSelectionWarning(null)
      return [...current, id]
    })
  }, [defaultOperatorKtuChartIds])

  const toggleProjectTimeInChart = useCallback((id: string) => {
    setProjectTimeChartIds((currentIds) => {
      const current = currentIds ?? defaultProjectTimeChartIds
      if (current.includes(id)) {
        setProjectTimeSelectionWarning(null)
        return current.filter((item) => item !== id)
      }
      if (current.length >= 10) {
        setProjectTimeSelectionWarning('На графике времени можно одновременно показать максимум 10 проектов.')
        return current
      }
      setProjectTimeSelectionWarning(null)
      return [...current, id]
    })
  }, [defaultProjectTimeChartIds])

  const operatorSummary = useMemo(() => {
    const current = visibleOperators.reduce((sum, op) => sum + (Number(op.closed_tickets) || 0), 0)
    const previous = visiblePrevOperators.reduce((sum, op) => sum + (Number(op.closed_tickets) || 0), 0)
    return changeLine(current, previous)
  }, [visibleOperators, visiblePrevOperators])

  const productSummary = useMemo(() => changeLine(Number(stats?.total_tickets) || 0, Number(prevStats?.total_tickets) || 0, true), [stats?.total_tickets, prevStats?.total_tickets])
  const aiTransferredSummary = useMemo(() => changeLine(Number(aiOps?.ai_transferred) || 0, Number(prevAiOps?.ai_transferred) || 0, true), [aiOps?.ai_transferred, prevAiOps?.ai_transferred])
  const aiInProgressSummary = useMemo(() => changeLine(Number(aiOps?.ai_in_progress) || 0, Number(prevAiOps?.ai_in_progress) || 0, true), [aiOps?.ai_in_progress, prevAiOps?.ai_in_progress])

  const totalClosedOperators = useMemo(() => visibleOperators.reduce((sum, op) => sum + (Number(op.closed_tickets) || 0), 0), [visibleOperators])
  const totalProducts = Number(stats?.total_tickets) || productRows.reduce((sum, row) => sum + (Number(row.total) || 0), 0)
  const overviewSourceRows = useMemo(() => normalizeChannelStats(stats?.by_channel), [stats?.by_channel])
  const prevOverviewSourceMap = useMemo(() => new Map(normalizeChannelStats(prevStats?.by_channel).map((row) => [channelSourceKey(row), Number(row.total) || 0])), [prevStats?.by_channel])
  const totalOverviewSources = overviewSourceRows.reduce((sum, row) => sum + (Number(row.total) || 0), 0)
  const sourceSummary = useMemo(() => changeLine(totalOverviewSources, normalizeChannelStats(prevStats?.by_channel).reduce((sum, row) => sum + (Number(row.total) || 0), 0), true), [prevStats?.by_channel, totalOverviewSources])
  const overviewProjectSourceRows = useMemo(() => normalizeProjectChannelStats(stats?.by_project_channel), [stats?.by_project_channel])
  const prevOverviewProjectSourceRows = useMemo(() => normalizeProjectChannelStats(prevStats?.by_project_channel), [prevStats?.by_project_channel])
  const aiSourceRows = useMemo(() => normalizeChannelStats(aiOps?.by_channel), [aiOps?.by_channel])
  const prevAiSourceMap = useMemo(() => new Map(normalizeChannelStats(prevAiOps?.by_channel).map((row) => [channelSourceKey(row), Number(row.total) || 0])), [prevAiOps?.by_channel])
  const totalAiSources = aiSourceRows.reduce((sum, row) => sum + (Number(row.total) || 0), 0)
  const aiSourceSummary = useMemo(() => changeLine(totalAiSources, normalizeChannelStats(prevAiOps?.by_channel).reduce((sum, row) => sum + (Number(row.total) || 0), 0), true), [prevAiOps?.by_channel, totalAiSources])
  const aiProjectSourceRows = useMemo(() => normalizeProjectChannelStats(aiOps?.by_project_channel), [aiOps?.by_project_channel])
  const prevAiProjectSourceRows = useMemo(() => normalizeProjectChannelStats(prevAiOps?.by_project_channel), [prevAiOps?.by_project_channel])
  const prevOperatorMap = useMemo(() => new Map(visiblePrevOperators.map((op) => [op.operator_id || op.operator_name || '', Number(op.closed_tickets) || 0])), [visiblePrevOperators])
  const prevProductMap = useMemo(() => new Map((prevStats?.by_project ?? []).map((row) => [row.project_id || '', Number(row.total) || 0])), [prevStats?.by_project])
  const makeProductTooltipData = useCallback((row: ProductStatsRow, project?: ProjectRow | null): ProductTooltipData => {
    const total = Number(row.total) || 0
    return {
      name: project?.name || row.project_name || '—',
      color: project?.color || '#3B82F6',
      total,
      previous: prevProductMap.get(row.project_id || '') || 0,
      percent: totalProducts > 0 ? (total / totalProducts) * 100 : 0,
      opened: Number(row.opened) || 0,
      inWork: Number(row.in_work) || 0,
      resolved: Number(row.resolved) || 0,
      closed: Number(row.closed) || 0,
      emergency: Number(row.emergency) || 0,
      avgFirstResponseMinutes: row.avg_first_response_minutes,
      avgCloseMinutes: row.avg_close_minutes,
    }
  }, [prevProductMap, totalProducts])
  const prevOperatorKtuMap = useMemo(
    () => new Map(prevKtuOperatorsDetailed.filter((op) => op.ktu != null).map((op) => [op.operator_id || op.operator_name || '', Number(op.ktu) || 0])),
    [prevKtuOperatorsDetailed],
  )
  const prevProjectResponseMap = useMemo(
    () => new Map((prevStats?.by_project ?? []).map((row) => [row.project_id || '', Number(row.avg_first_response_minutes) || 0])),
    [prevStats?.by_project],
  )
  const prevProjectCloseMap = useMemo(
    () => new Map((prevStats?.by_project ?? []).map((row) => [row.project_id || '', Number(row.avg_close_minutes) || 0])),
    [prevStats?.by_project],
  )
  const aiReasonRows = useMemo(() => ([
    { key: 'transfer_after_system_hint', label: aiReasonLabels.transfer_after_system_hint, current: Number(aiOps?.transfer_after_system_hint?.count) || 0, previous: Number(prevAiOps?.transfer_after_system_hint?.count) || 0, pctTransfers: Number(aiOps?.transfer_after_system_hint?.pct_of_transfers) || 0, pctTotal: Number(aiOps?.transfer_after_system_hint?.pct_of_total_tickets) || 0 },
    { key: 'transfer_planned_instruction', label: aiReasonLabels.transfer_planned_instruction, current: Number(aiOps?.transfer_planned_instruction?.count) || 0, previous: Number(prevAiOps?.transfer_planned_instruction?.count) || 0, pctTransfers: Number(aiOps?.transfer_planned_instruction?.pct_of_transfers) || 0, pctTotal: Number(aiOps?.transfer_planned_instruction?.pct_of_total_tickets) || 0 },
    { key: 'transfer_unknown_answer', label: aiReasonLabels.transfer_unknown_answer, current: Number(aiOps?.transfer_unknown_answer?.count) || 0, previous: Number(prevAiOps?.transfer_unknown_answer?.count) || 0, pctTransfers: Number(aiOps?.transfer_unknown_answer?.pct_of_transfers) || 0, pctTotal: Number(aiOps?.transfer_unknown_answer?.pct_of_total_tickets) || 0 },
    { key: 'transfer_user_requested_operator', label: aiReasonLabels.transfer_user_requested_operator, current: Number(aiOps?.transfer_user_requested_operator?.count) || 0, previous: Number(prevAiOps?.transfer_user_requested_operator?.count) || 0, pctTransfers: Number(aiOps?.transfer_user_requested_operator?.pct_of_transfers) || 0, pctTotal: Number(aiOps?.transfer_user_requested_operator?.pct_of_total_tickets) || 0 },
    { key: 'transfer_watchdog_timeout', label: aiReasonLabels.transfer_watchdog_timeout, current: Number(aiOps?.transfer_watchdog_timeout?.count) || 0, previous: Number(prevAiOps?.transfer_watchdog_timeout?.count) || 0, pctTransfers: Number(aiOps?.transfer_watchdog_timeout?.pct_of_transfers) || 0, pctTotal: Number(aiOps?.transfer_watchdog_timeout?.pct_of_total_tickets) || 0 },
    { key: 'transfer_watchdog_requeue_limit', label: aiReasonLabels.transfer_watchdog_requeue_limit, current: Number(aiOps?.transfer_watchdog_requeue_limit?.count) || 0, previous: Number(prevAiOps?.transfer_watchdog_requeue_limit?.count) || 0, pctTransfers: Number(aiOps?.transfer_watchdog_requeue_limit?.pct_of_transfers) || 0, pctTotal: Number(aiOps?.transfer_watchdog_requeue_limit?.pct_of_total_tickets) || 0 },
    { key: 'transfer_other', label: aiReasonLabels.transfer_other, current: Number(aiOps?.transfer_other?.count) || 0, previous: Number(prevAiOps?.transfer_other?.count) || 0, pctTransfers: Number(aiOps?.transfer_other?.pct_of_transfers) || 0, pctTotal: Number(aiOps?.transfer_other?.pct_of_total_tickets) || 0 },
  ]), [aiOps, prevAiOps])
  const aiProjectRows = useMemo(
    () => [...(aiOps?.by_project ?? [])].sort((a, b) => (Number(b.non_emergency_tickets) || 0) - (Number(a.non_emergency_tickets) || 0)),
    [aiOps?.by_project],
  )
  const prevAiProjectMap = useMemo(
    () => new Map((prevAiOps?.by_project ?? []).map((row) => [row.project_id, row])),
    [prevAiOps?.by_project],
  )
  const operatorKtuSummary = useMemo(() => {
    const currentValues = topOperatorKtu.filter((op) => op.ktu != null).map((op) => Number(op.ktu))
    const previousValues = topOperatorKtu.map((op) => prevOperatorKtuMap.get(op.operator_id || op.operator_name || '')).filter((value): value is number => value != null)
    const current = currentValues.length ? currentValues.reduce((sum, value) => sum + value, 0) / currentValues.length : null
    const previous = previousValues.length ? previousValues.reduce((sum, value) => sum + value, 0) / previousValues.length : null
    return compactChangeLine(current, previous) ?? { text: 'Нет данных для сравнения', tone: 'gray' as const }
  }, [topOperatorKtu, prevOperatorKtuMap])
  const projectResponseSummary = useMemo(() => {
    const selected = new Set(topProjectTime.map((row) => row.project_id))
    const current = aggregateTiming(topProjectTime, 'first_response')
    const previous = aggregateTiming((prevStats?.by_project ?? []).filter((row) => selected.has(row.project_id)), 'first_response')
    return compactChangeLine(current, previous, true) ?? { text: 'Нет данных для сравнения', tone: 'gray' as const }
  }, [topProjectTime, prevStats?.by_project])
  const projectCloseSummary = useMemo(() => {
    const selected = new Set(topProjectTime.map((row) => row.project_id))
    const current = aggregateTiming(topProjectTime, 'resolution')
    const previous = aggregateTiming((prevStats?.by_project ?? []).filter((row) => selected.has(row.project_id)), 'resolution')
    return compactChangeLine(current, previous, true) ?? { text: 'Нет данных для сравнения', tone: 'gray' as const }
  }, [topProjectTime, prevStats?.by_project])

  const visibleOperatorTimes = useMemo(
    () => [...operatorTimes].filter((op) => !isAIOperator(op)).sort((a, b) => (a.operator_name || '').localeCompare(b.operator_name || '', 'ru')),
    [operatorTimes],
  )
  const prevOperatorTimesMap = useMemo(
    () => new Map(prevOperatorTimes.map((op) => [operatorKey(op), op])),
    [prevOperatorTimes],
  )
  const avgResolutionTimeSummary = useMemo(() => {
    const current = aggregateTiming(visibleOperatorTimes, 'resolution')
    const previous = aggregateTiming(prevOperatorTimes.filter((op) => !isAIOperator(op)), 'resolution')
    return { current, previous, delta: compactChangeLine(current, previous, true) }
  }, [visibleOperatorTimes, prevOperatorTimes])
  const avgFirstResponseTimeSummary = useMemo(() => {
    const current = aggregateTiming(visibleOperatorTimes, 'first_response')
    const previous = aggregateTiming(prevOperatorTimes.filter((op) => !isAIOperator(op)), 'first_response')
    return { current, previous, delta: compactChangeLine(current, previous, true) }
  }, [visibleOperatorTimes, prevOperatorTimes])
  const avgAssignmentTimeSummary = useMemo(() => {
    const current = aggregateTiming(visibleOperatorTimes, 'assignment')
    const previous = aggregateTiming(prevOperatorTimes.filter((op) => !isAIOperator(op)), 'assignment')
    return { current, previous, delta: compactChangeLine(current, previous, true) }
  }, [visibleOperatorTimes, prevOperatorTimes])
  const avgReplyTimeSummary = useMemo(() => {
    const current = aggregateTiming(visibleOperatorTimes, 'reply')
    const previous = aggregateTiming(prevOperatorTimes.filter((op) => !isAIOperator(op)), 'reply')
    return { current, previous, delta: compactChangeLine(current, previous, true) }
  }, [visibleOperatorTimes, prevOperatorTimes])

  const hideChartRowsTooltip = useCallback(() => {
    setChartRowsTooltip((current) => (current.visible ? { ...current, visible: false } : current))
  }, [])

  const showChartRowsTooltip = useCallback((chart: ChartJS, tooltip: { caretX: number; caretY: number }, data: ChartRowsTooltipData) => {
    const canvasRect = chart.canvas.getBoundingClientRect()
    const viewportX = canvasRect.left + tooltip.caretX
    const viewportY = canvasRect.top + tooltip.caretY
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || chart.width
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || chart.height
    const height = 54 + data.rows.length * 30
    setChartRowsTooltip((current) => {
      if (
        current.visible &&
        current.viewportX === viewportX &&
        current.viewportY === viewportY &&
        current.height === height &&
        current.data?.title === data.title &&
        current.data?.rows.length === data.rows.length
      ) {
        return current
      }
      return { visible: true, viewportX, viewportY, viewportWidth, viewportHeight, height, data }
    })
  }, [])

  const chartOptions: ChartOptions<'bar'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        ...chartTooltipBase,
        callbacks: { title: (items) => items[0]?.label || '', label: (item) => `${item.dataset.label}: ${formatNumber(Number(item.raw) || 0)}` },
      },
    },
    scales: {
      x: { grid: { display: false }, border: { display: false }, ticks: { color: '#64748b', maxRotation: 0, minRotation: 0, font: { size: 12 } } },
      y: { beginAtZero: true, grid: { color: 'rgba(148, 163, 184, 0.45)', lineWidth: 1.2 }, border: { display: false }, ticks: { color: 'var(--text-primary)', precision: 0, font: { size: 14, weight: 400 }, padding: 10 } },
    },
    onHover: (_event, elements, chart) => { chart.canvas.style.cursor = elements.length ? 'pointer' : 'default' },
  }), [])

  const stackedProjectSourceChartOptions: ChartOptions<'bar'> = useMemo(() => ({
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      x: {
        ...chartOptions.scales?.x,
        stacked: true,
        ticks: { display: false },
      },
      y: {
        ...chartOptions.scales?.y,
        stacked: true,
      },
    },
  }), [chartOptions])

  const operatorChartData = useMemo(() => buildOperatorChartData(topOperators, prevOperatorMap), [topOperators, prevOperatorMap])

  const productChartData = useMemo(() => buildProductChartData(topProducts, prevProductMap, projects), [topProducts, prevProductMap, projects])

  const overviewSourceChartData = useMemo(() => buildSourceChartData(overviewSourceRows, prevOverviewSourceMap), [overviewSourceRows, prevOverviewSourceMap])

  const overviewProjectSourceData = useMemo(() => buildProjectSourceChartData(overviewProjectSourceRows, prevOverviewProjectSourceRows, projects), [overviewProjectSourceRows, prevOverviewProjectSourceRows, projects])
  const projectSourceSummary = useMemo(() => changeLine(overviewProjectSourceData.total, overviewProjectSourceData.previousTotal, true), [overviewProjectSourceData])

  const operatorKtuChartData = useMemo(() => buildOperatorKtuChartData(topOperatorKtu, prevOperatorKtuMap), [topOperatorKtu, prevOperatorKtuMap])

  const projectTimeChartData = useMemo(
    () => buildProjectTimeChartData(topProjectTime, prevProjectResponseMap, prevProjectCloseMap, projects),
    [topProjectTime, prevProjectCloseMap, prevProjectResponseMap, projects],
  )

  const aiReasonChartData = useMemo(() => buildAiReasonChartData(aiReasonRows), [aiReasonRows])

  const topAiProjects = useMemo(() => aiProjectRows.slice(0, 10), [aiProjectRows])
  const aiProjectChartData = useMemo(() => buildAiProjectChartData(topAiProjects, prevAiProjectMap), [topAiProjects, prevAiProjectMap])

  const aiSourceChartData = useMemo(() => buildSourceChartData(aiSourceRows, prevAiSourceMap), [aiSourceRows, prevAiSourceMap])

  const aiProjectSourceData = useMemo(() => buildProjectSourceChartData(aiProjectSourceRows, prevAiProjectSourceRows, projects), [aiProjectSourceRows, prevAiProjectSourceRows, projects])
  const aiProjectSourceSummary = useMemo(() => changeLine(aiProjectSourceData.total, aiProjectSourceData.previousTotal, true), [aiProjectSourceData])

  const operatorChartOptions: ChartOptions<'bar'> = useMemo(() => ({
    ...chartOptions,
    layout: { padding: { bottom: 0 } },
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        enabled: false,
        external: ({ chart, tooltip }) => {
          if (!tooltip || tooltip.opacity === 0) {
            hideChartRowsTooltip()
            return
          }
          const index = tooltip.dataPoints?.[0]?.dataIndex
          const operator = index == null ? null : topOperators[index]
          if (!operator) {
            hideChartRowsTooltip()
            return
          }
          const name = operator.operator_name || operator.operator_email || '—'
          const current = Number(operator.closed_tickets) || 0
          const previous = prevOperatorMap.get(operator.operator_id || operator.operator_name || '') || 0
          showChartRowsTooltip(chart, tooltip, {
            title: name,
            color: operatorColors[index % operatorColors.length],
            imageUrl: resolveOperatorAvatarSrc(operator),
            rows: [
              { label: 'Закрыто', value: formatNumber(current), tone: 'blue' },
              { label: 'Предыдущий период', value: formatNumber(previous), tone: 'muted' },
              { label: 'Ср. ответ', value: formatMinutes(operator.avg_reply_minutes) },
              { label: 'Ср. закрытие', value: formatMinutes(operator.avg_close_minutes) },
            ],
          })
        },
      },
    },
    scales: {
      ...chartOptions.scales,
      x: {
        ...chartOptions.scales?.x,
        ticks: { display: false },
      },
    },
  }), [chartOptions, hideChartRowsTooltip, prevOperatorMap, showChartRowsTooltip, topOperators])

  const productChartOptions: ChartOptions<'bar'> = useMemo(() => ({
    ...chartOptions,
    layout: { padding: { bottom: 0 } },
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        enabled: false,
        external: ({ chart, tooltip }) => {
          if (!tooltip || tooltip.opacity === 0) {
            setProductTooltip((current) => (current.visible ? { ...current, visible: false } : current))
            return
          }
          const index = tooltip.dataPoints?.[0]?.dataIndex
          const row = index == null ? null : topProducts[index]
          if (!row) {
            setProductTooltip((current) => (current.visible ? { ...current, visible: false } : current))
            return
          }
          const project = projects.find((item) => item.id === row.project_id) ?? null
          const data = makeProductTooltipData(row, project)
          const canvasRect = chart.canvas.getBoundingClientRect()
          const viewportX = canvasRect.left + tooltip.caretX
          const viewportY = canvasRect.top + tooltip.caretY
          const viewportWidth = window.innerWidth || document.documentElement.clientWidth || chart.width
          const viewportHeight = window.innerHeight || document.documentElement.clientHeight || chart.height
          setProductTooltip((current) => {
            if (
              current.visible &&
              current.x === tooltip.caretX &&
              current.y === tooltip.caretY &&
              current.chartWidth === chart.width &&
              current.chartHeight === chart.height &&
              current.viewportX === viewportX &&
              current.viewportY === viewportY &&
              current.data?.name === data.name &&
              current.data?.total === data.total &&
              current.data?.previous === data.previous
            ) {
              return current
            }
            return { visible: true, x: tooltip.caretX, y: tooltip.caretY, chartWidth: chart.width, chartHeight: chart.height, viewportX, viewportY, viewportWidth, viewportHeight, data }
          })
        },
      },
    },
    scales: {
      ...chartOptions.scales,
      x: {
        ...chartOptions.scales?.x,
        ticks: { display: false },
      },
    },
  }), [chartOptions, topProducts, projects, makeProductTooltipData])

  const ktuChartOptions: ChartOptions<'bar'> = useMemo(() => ({
    ...chartOptions,
    layout: { padding: { bottom: 0 } },
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales?.y,
        ticks: {
          color: 'var(--text-primary)',
          font: { size: 14, weight: 400 },
          padding: 10,
          callback: (value) => formatKtu(Number(value)),
        },
      },
      x: {
        ...chartOptions.scales?.x,
        ticks: { display: false },
      },
    },
  }), [chartOptions])

  const operatorKtuChartOptions: ChartOptions<'bar'> = useMemo(() => ({
    ...ktuChartOptions,
    plugins: {
      ...ktuChartOptions.plugins,
      tooltip: {
        enabled: false,
        external: ({ chart, tooltip }) => {
          if (!tooltip || tooltip.opacity === 0) {
            hideChartRowsTooltip()
            return
          }
          const index = tooltip.dataPoints?.[0]?.dataIndex
          const operator = index == null ? null : topOperatorKtu[index]
          if (!operator) {
            hideChartRowsTooltip()
            return
          }
          const name = operator.operator_name || operator.operator_email || '—'
          const current = Number(operator.ktu) || 0
          const previous = prevOperatorKtuMap.get(operator.operator_id || operator.operator_name || '') || 0
          showChartRowsTooltip(chart, tooltip, {
            title: name,
            color: operatorColors[index % operatorColors.length],
            imageUrl: resolveOperatorAvatarSrc(operator),
            rows: [
              { label: 'КТУ', value: formatKtu(current), tone: 'blue' },
              { label: 'Предыдущий период', value: formatKtu(previous), tone: 'muted' },
              { label: 'База баллов', value: formatKtu(operator.ktu_base_points) },
              { label: 'Коэф. скорости', value: formatKtu(operator.ktu_speed_factor) },
              { label: 'Эффективные часы', value: formatNumber(Number(operator.working_hours) || 0) },
            ],
          })
        },
      },
    },
  }), [hideChartRowsTooltip, ktuChartOptions, prevOperatorKtuMap, showChartRowsTooltip, topOperatorKtu])

  const projectTimeChartOptions: ChartOptions<'bar'> = useMemo(() => ({
    ...chartOptions,
    layout: { padding: { bottom: 0 } },
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        enabled: false,
        external: ({ chart, tooltip }) => {
          if (!tooltip || tooltip.opacity === 0) {
            hideChartRowsTooltip()
            return
          }
          const index = tooltip.dataPoints?.[0]?.dataIndex
          const row = index == null ? null : topProjectTime[index]
          if (!row) {
            hideChartRowsTooltip()
            return
          }
          const project = projects.find((item) => item.id === row.project_id)
          const responsePrev = prevProjectResponseMap.get(row.project_id || '') || 0
          const closePrev = prevProjectCloseMap.get(row.project_id || '') || 0
          showChartRowsTooltip(chart, tooltip, {
            title: project?.name || row.project_name || '—',
            color: project?.color || '#3B82F6',
            imageUrl: project?.icon_url ? resolveUploadsUrl(project.icon_url) : undefined,
            emoji: project?.icon && !project.icon_url ? project.icon : undefined,
            rows: [
              { label: 'Ср. ответ', value: formatDurationClock(Number(row.avg_first_response_minutes) || 0), tone: 'blue' },
              { label: 'Пред. ответ', value: formatDurationClock(responsePrev), tone: 'muted' },
              { label: 'Ср. решение', value: formatDurationClock(Number(row.avg_close_minutes) || 0) },
              { label: 'Пред. решение', value: formatDurationClock(closePrev), tone: 'muted' },
            ],
          })
        },
      },
    },
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales?.y,
        ticks: {
          color: 'var(--text-primary)',
          font: { size: 12, weight: 400 },
          padding: 10,
          callback: (value) => formatDurationClock(Number(value)),
        },
      },
      x: {
        ...chartOptions.scales?.x,
        ticks: { display: false },
      },
    },
  }), [chartOptions, hideChartRowsTooltip, prevProjectCloseMap, prevProjectResponseMap, projects, showChartRowsTooltip, topProjectTime])

  const aiReasonChartOptions: ChartOptions<'bar'> = useMemo(() => ({
    ...chartOptions,
    indexAxis: 'y',
    layout: { padding: { bottom: 0 } },
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        enabled: false,
        external: ({ chart, tooltip }) => {
          if (!tooltip || tooltip.opacity === 0) {
            hideChartRowsTooltip()
            return
          }
          const index = tooltip.dataPoints?.[0]?.dataIndex
          const row = index == null ? null : aiReasonRows[index]
          if (!row) {
            hideChartRowsTooltip()
            return
          }
          showChartRowsTooltip(chart, tooltip, {
            title: row.label,
            rows: [
              { label: 'Текущий период', value: formatNumber(row.current), tone: 'blue' },
              { label: 'Предыдущий период', value: formatNumber(row.previous), tone: 'muted' },
              { label: '% от передач', value: formatPercent(row.pctTransfers) },
              { label: '% от всех AI-обращений', value: formatPercent(row.pctTotal) },
            ],
          })
        },
      },
    },
    scales: {
      ...chartOptions.scales,
      x: {
        ...chartOptions.scales?.y,
        beginAtZero: true,
        ticks: { color: 'var(--text-primary)', precision: 0, font: { size: 12, weight: 400 }, padding: 10 },
      },
      y: {
        ...chartOptions.scales?.x,
        ticks: { color: '#64748b', font: { size: 11 }, autoSkip: false },
      },
    },
    onClick: (_event, elements) => {
      if (customPeriodPending) return
      const index = elements[0]?.index
      const row = index == null ? null : aiReasonRows[index]
      if (!row) return
      setAiReasonModal({ reason: row.key as AiReasonKey, label: row.label })
    },
  }), [aiReasonRows, chartOptions, customPeriodPending, hideChartRowsTooltip, showChartRowsTooltip])

  const aiProjectChartOptions: ChartOptions<'bar'> = useMemo(() => ({
    ...chartOptions,
    layout: { padding: { bottom: 0 } },
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        enabled: false,
        external: ({ chart, tooltip }) => {
          if (!tooltip || tooltip.opacity === 0) {
            hideChartRowsTooltip()
            return
          }
          const index = tooltip.dataPoints?.[0]?.dataIndex
          const row = index == null ? null : topAiProjects[index]
          if (!row) {
            hideChartRowsTooltip()
            return
          }
          const project = projects.find((item) => item.id === row.project_id)
          const previous = prevAiProjectMap.get(row.project_id)
          showChartRowsTooltip(chart, tooltip, {
            title: project?.name || row.project_name || row.project_id || '—',
            color: project?.color || '#3B82F6',
            imageUrl: project?.icon_url ? resolveUploadsUrl(project.icon_url) : undefined,
            emoji: project?.icon && !project.icon_url ? project.icon : undefined,
            rows: [
              { label: 'База без аварий', value: formatNumber(row.non_emergency_tickets), tone: 'blue' },
              { label: 'Предыдущий период', value: formatNumber(previous?.non_emergency_tickets), tone: 'muted' },
              { label: 'Всего обращений', value: formatNumber(row.total_tickets) },
              { label: 'Передано оператору', value: `${formatNumber(row.ai_transferred)} (${formatPercent(row.ai_transferred_pct_of_total)})` },
              { label: 'Решено AI', value: `${formatNumber(row.ai_resolved_closed)} (${formatPercent(row.ai_resolved_closed_pct_of_total)})` },
              { label: 'В работе у AI', value: `${formatNumber(row.ai_in_progress)} (${formatPercent(row.ai_in_progress_pct_of_total)})` },
              { label: 'Аварии', value: formatNumber(row.emergency_tickets), tone: 'danger' },
            ],
          })
        },
      },
    },
    scales: {
      ...chartOptions.scales,
      x: {
        ...chartOptions.scales?.x,
        ticks: { display: false },
      },
    },
  }), [chartOptions, hideChartRowsTooltip, prevAiProjectMap, projects, showChartRowsTooltip, topAiProjects])

  const slaTopStats = slaSummaryStats
  const prevSlaTopStats = prevSlaSummaryStats
  const slaTimelineStats = slaSectionStats.timeline
  const slaProductStats = slaSectionStats.product
  const slaChannelStats = slaSectionStats.channel
  const slaQueueStats = slaSectionStats.queue
  const slaAgentStats = slaSectionStats.agent
  const slaAgentRows = slaAgentStats?.sla?.by_agent || []
  const slaAgentInWork = slaAgentRows.reduce((sum, row) => sum + Number(row.in_work ?? 0), 0)
  const slaAgentProcessed = slaAgentRows.reduce((sum, row) => sum + Number(row.processed ?? 0), 0)
  const slaAgentAtRisk = slaAgentRows.reduce((sum, row) => sum + Number(row.at_risk ?? 0), 0)
  const slaAgentBreached = slaAgentRows.reduce((sum, row) => sum + Number(row.breached ?? 0), 0)
  const slaAgentRisk = slaAgentRows.reduce<SlaBreakdownRow | null>((top, row) => {
    if (!top) return row
    return Number(row.breached ?? 0) > Number(top.breached ?? 0) ? row : top
  }, null)
  const slaTimeline = (slaTimelineStats?.sla?.timeline?.[slaChartMetric] || []) as SlaTimelinePoint[]
  const slaTimelineCurrentActual = useMemo(() => aggregateSlaTimeline(slaTimeline), [slaTimeline])
  const slaTimelinePreviousActual = useMemo(() => aggregateSlaTimeline(slaTimeline, true), [slaTimeline])
  const slaLocalFilterProps = (key: SlaFilterKey) => ({
    period: slaFilters[key].period,
    onPeriodChange: (nextPeriod: DashboardPeriodKey) => {
      if (nextPeriod === 'custom_datetime') {
        // Already-applied main custom: only this section switches; dates stay applied.
        if (period === 'custom_datetime' && customApplied) {
          updateSlaFilter(key, { period: 'custom_datetime' })
          return
        }
        // Open main date/apply UI without syncing other sections' independent filters.
        setPeriod('custom_datetime')
        setCustomApplied(false)
        updateSlaFilter(key, { period: 'custom_datetime' })
        setSlaSectionStats((prev) => ({ ...prev, [key]: null }))
        return
      }
      updateSlaFilter(key, { period: nextPeriod })
    },
    projectIds: slaFilters[key].projectIds,
    onProjectIdsChange: (nextProjectIds: string[]) => {
      if (customPeriodPending) return
      updateSlaFilter(key, { projectIds: nextProjectIds })
    },
    projects,
    onRefresh: customPeriodPending
      ? () => {}
      : () => { void loadSlaSection(key, slaFilters[key]) },
    loading: slaSectionLoading[key],
  })
  const slaLineData = useMemo(() => ({
    labels: slaTimeline.map((point) => point.hour),
    datasets: [
      {
        label: 'Цель',
        data: slaTimeline.map(() => 100),
        borderColor: SLA_GREEN,
        backgroundColor: SLA_GREEN,
        borderWidth: 2,
        pointRadius: 0,
        tension: 0,
      },
      {
        label: 'Фактический SLA',
        data: slaTimeline.map((point) => point.actual_percent ?? null),
        borderColor: SLA_BLUE,
        backgroundColor: '#FFFFFF',
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBorderWidth: 2,
        tension: 0.25,
      },
      {
        label: 'Предыдущий период',
        data: slaTimeline.map((point) => point.previous_percent ?? null),
        borderColor: '#AAB2C3',
        backgroundColor: '#AAB2C3',
        borderWidth: 2,
        borderDash: [6, 6],
        pointRadius: 0,
        tension: 0.25,
      },
    ],
  }), [slaTimeline])

  const slaLineOptions: ChartOptions<'line'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: 'index' },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        align: 'start',
        labels: { usePointStyle: true, pointStyle: 'line', boxWidth: 42, color: '#1F2937', padding: 22 },
      },
      tooltip: {
        ...chartTooltipBase,
        callbacks: {
          title: (items) => items[0]?.label || '',
          label: (item) => `${item.dataset.label}: ${slaPercent(Number(item.raw), 1)}`,
          afterBody: (items) => {
            const index = items[0]?.dataIndex ?? 0
            const point = slaTimeline[index]
            if (!point) return []
            return [`Обращения: ${formatNumber(point.tickets)}`, `Нарушено: ${formatNumber(point.breached)}`]
          },
        },
      },
    },
    scales: {
      y: {
        min: 0,
        max: 110,
        ticks: { callback: (value) => `${value}%`, color: '#1F2937', stepSize: 25 },
        grid: { color: '#DFE3EA' },
      },
      x: {
        ticks: { color: '#1F2937', maxRotation: 0, autoSkip: false, font: { size: 10 } },
        grid: { display: false },
      },
    },
  }), [slaTimeline])

  if (!canAnalytics) {
    return <PageShell><Alert color="gray" title="Нет доступа">Недостаточно прав для раздела «Аналитика New».</Alert></PageShell>
  }

  return (
    <PageShell>
      <AnalyticsTooltipProvider>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" gap="md">
          <Stack gap={4}>
            <Title order={2}>Аналитика New</Title>
            <Text c="dimmed" size="sm">Обновлённый обзор за {getDashboardPeriodLabel(period)}.</Text>
          </Stack>
        </Group>

        <AnalyticsPeriodFilters period={period} onPeriodChange={handlePeriodChange} projectIds={projectIds} onProjectIdsChange={handleProjectIdsChange} projects={projects} customFrom={customFrom} customTo={customTo} onCustomFromChange={setCustomFrom} onCustomToChange={setCustomTo} customApplied={customApplied} onCustomApply={handleCustomApply} onRefresh={customPeriodPending ? () => {} : onRefresh} loading={loading} />
        {customPeriodPending ? <Alert color="blue" title="Свой период">Укажите даты и нажмите «Показать», чтобы загрузить данные.</Alert> : null}

        {dashboardError ? <Alert color="red" title="Ошибка продуктов">{dashboardError}</Alert> : null}
        {slaSummaryError ? <Alert color="red" title="Ошибка SLA">{slaSummaryError}</Alert> : null}
        {operatorKtuError ? <Alert color="red" title="Ошибка КТУ">{operatorKtuError}</Alert> : null}
        {operatorsError ? <Alert color="red" title="Ошибка операторов">{operatorsError}</Alert> : null}
        {aiOpsError ? <Alert color="red" title="Ошибка Мозгобота">{aiOpsError}</Alert> : null}
        {analyticsTab === 'ai-ops' && aiOps?.ai_operator_count === 0 ? <Alert color="orange" title="Мозгобот не найден">В рабочем пространстве не найдена учётная запись Мозгобота. Его показатели недоступны.</Alert> : null}
        {operatorTimesError ? <Alert color="red" title="Ошибка времени операторов">{operatorTimesError}</Alert> : null}

        <Tabs value={analyticsTab} onChange={(value) => setAnalyticsTab((value as 'overview' | 'sla' | 'ai-ops' | 'operator-times') || 'overview')}>
          <Tabs.List>
            <Tabs.Tab value="overview">Обзор</Tabs.Tab>
            <Tabs.Tab value="sla">SLA</Tabs.Tab>
            <Tabs.Tab value="ai-ops">Мозгобот</Tabs.Tab>
            <Tabs.Tab value="operator-times">Время операторов</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {analyticsTab === 'overview' ? (
          <>
        <MainInfoSummary stats={stats} prevStats={prevStats} loading={dashboardLoading} />

        <Group align="stretch" gap="md" wrap="wrap">
          <AnalyticsChartCard title="Статистика по операторам" summary={operatorSummary} topNotice={operatorSelectionWarning || (chartVisibleOperators.length > 10 ? `На графике выбрано ${topOperators.length} из ${chartVisibleOperators.length}. Больше закрытых обращений — лучше.` : 'Больше закрытых обращений — лучше.')} loading={operatorsLoading} loadingLabel="Загружаем операторов" right={visibleOperators.map((op, index) => {
            const id = operatorKey(op)
            const value = Number(op.closed_tickets) || 0
            const previous = prevOperatorMap.get(op.operator_id || op.operator_name || '') || 0
            const pct = totalClosedOperators > 0 ? ((value / totalClosedOperators) * 100).toFixed(1) : '0'
            const color = operatorColors[index % operatorColors.length]
            return <LegendOperatorRow key={id} op={op} value={value} previous={previous} pct={pct} color={color} selected={activeOperatorChartIds.includes(id)} onToggle={() => toggleOperatorInChart(id)} />
          })}>
            <Stack gap={4} h="100%">
              <Box style={{ flex: '1 1 auto', minHeight: 0 }}>
                <Bar ref={operatorChartRef} data={operatorChartData} options={{ ...operatorChartOptions, onClick: (_event, elements) => {
                  if (customPeriodPending) return
                  const index = elements[0]?.index
                  const op = index == null ? null : topOperators[index]
                  if (!op?.operator_id) return
                  setOperatorModal({ operatorId: op.operator_id, block: 'closed_tickets', label: `Закрытые: ${op.operator_name || 'оператор'}` })
                } }} />
              </Box>
              <AxisIconRow
                chartRef={operatorChartRef}
                items={topOperators.map((op, index) => {
                  const name = op.operator_name || op.operator_email || '—'
                  const operatorId = op.operator_id || ''
                  const value = Number(op.closed_tickets) || 0
                  const previous = prevOperatorMap.get(op.operator_id || op.operator_name || '') || 0
                  return {
                    key: operatorId || `${name}-${index}`,
                    name,
                    color: operatorColors[index % operatorColors.length],
                    imageUrl: resolveOperatorAvatarSrc(op),
                    onClick: operatorId && !customPeriodPending ? () => setOperatorModal({ operatorId, block: 'closed_tickets', label: `Закрытые: ${name}` }) : undefined,
                    tooltip: <AnalyticsTooltipContent title={name} rows={[{ label: 'Закрыто', value: formatNumber(value), tone: 'primary' }, { label: 'Предыдущий период', value: formatNumber(previous), tone: 'muted' }, { label: 'Ср. ответ', value: formatMinutes(op.avg_reply_minutes) }, { label: 'Ср. закрытие', value: formatMinutes(op.avg_close_minutes) }]} />,
                  }
                })}
              />
            </Stack>
          </AnalyticsChartCard>

          <AnalyticsChartCard title="Статистика по продуктам" summary={productSummary} topNotice={productSelectionWarning || (productRows.length > 10 ? `На графике выбрано ${topProducts.length} из ${productRows.length}. Меньше обращений — лучше для общей нагрузки.` : 'Меньше обращений — лучше для общей нагрузки.')} loading={dashboardLoading} loadingLabel="Загружаем продукты" withoutAI={withoutAIProducts} onWithoutAIChange={setWithoutAIProducts} right={productRows.map((row) => {
            const id = productKey(row)
            const project = projects.find((p) => p.id === row.project_id)
            const value = Number(row.total) || 0
            const pct = totalProducts > 0 ? ((value / totalProducts) * 100).toFixed(1) : '0'
            return <LegendProductRow key={id} name={project?.name || row.project_name || '—'} color={project?.color || '#3B82F6'} imageUrl={project?.icon_url ? resolveUploadsUrl(project.icon_url) : undefined} emoji={project?.icon && !project.icon_url ? project.icon : undefined} value={value} pct={pct} selected={activeProductChartIds.includes(id)} onToggle={() => toggleProductInChart(id)} />
          })}>
            <Stack gap={4} h="100%">
              <Box style={{ flex: '1 1 auto', minHeight: 0, position: 'relative' }}>
                <Bar ref={productChartRef} data={productChartData} options={{ ...productChartOptions, onClick: (_event, elements) => {
                  if (customPeriodPending) return
                  const index = elements[0]?.index
                  const row = index == null ? null : topProducts[index]
                  const project = row ? projects.find((p) => p.id === row.project_id) ?? null : null
                  if (project) setProductModal(project)
                } }} />
                {productTooltip.visible && productTooltip.data ? (
                  <Box
                    style={{
                      position: 'fixed',
                      left: Math.max(8, Math.min(productTooltip.viewportX + 14, Math.max(8, productTooltip.viewportWidth - ANALYTICS_TOOLTIP_WIDTH - 8))),
                      top: Math.max(8, Math.min(productTooltip.viewportY - 110, Math.max(8, productTooltip.viewportHeight - PRODUCT_TOOLTIP_HEIGHT - 8))),
                      zIndex: 5000,
                      pointerEvents: 'none',
                    }}
                  >
                    <ProductChartTooltipContent data={productTooltip.data} />
                  </Box>
                ) : null}
              </Box>
              <AxisIconRow
                chartRef={productChartRef}
                items={topProducts.map((row, index) => {
                  const project = projects.find((p) => p.id === row.project_id)
                  const name = project?.name || row.project_name || '—'
                  return {
                    key: row.project_id || `${name}-${index}`,
                    name,
                    color: project?.color || '#3B82F6',
                    imageUrl: project?.icon_url ? resolveUploadsUrl(project.icon_url) : undefined,
                    emoji: project?.icon && !project.icon_url ? project.icon : undefined,
                    onClick: project && !customPeriodPending ? () => setProductModal(project) : undefined,
                    tooltipVariant: 'product' as const,
                    tooltip: <ProductChartTooltipContent data={makeProductTooltipData(row, project)} />,
                  }
                })}
              />
            </Stack>
          </AnalyticsChartCard>

        </Group>

        <HourlyDistributionCard
          stats={sameHourlyAsDashboard ? stats : hourlyStats}
          prevStats={sameHourlyAsDashboard ? prevStats : prevHourlyStats}
          projects={projects}
          loading={sameHourlyAsDashboard ? dashboardLoading : hourlyLoading}
          withoutAI={withoutAIHourly}
          onWithoutAIChange={setWithoutAIHourly}
          basis={hourlyBasis}
          onBasisChange={setHourlyBasis}
        />
          </>
        ) : null}

        {analyticsTab === 'sla' ? (
        <Stack gap="md">
          <Box pos="relative">
            {slaSummaryLoading ? <BlockLoader label="Загружаем SLA" /> : null}
            <Stack gap="sm" opacity={slaSummaryLoading ? 0.45 : 1}>
              <SimpleGrid cols={{ base: 1, md: 3 }} spacing={8}>
                <SlaHeroCard
                  title="Actual SLA"
                  metric={{ target_minutes: null, total: (slaTopStats?.sla?.first_response?.total ?? 0) + (slaTopStats?.sla?.resolution?.total ?? 0), met: (slaTopStats?.sla?.first_response?.met ?? 0) + (slaTopStats?.sla?.resolution?.met ?? 0), breached: slaTopStats?.sla?.breach_count ?? 0, pending: (slaTopStats?.sla?.first_response?.pending ?? 0) + (slaTopStats?.sla?.resolution?.pending ?? 0), actual_percent: slaTopStats?.sla?.actual_percent ?? null }}
                  previous={{ target_minutes: null, total: (prevSlaTopStats?.sla?.first_response?.total ?? 0) + (prevSlaTopStats?.sla?.resolution?.total ?? 0), met: (prevSlaTopStats?.sla?.first_response?.met ?? 0) + (prevSlaTopStats?.sla?.resolution?.met ?? 0), breached: prevSlaTopStats?.sla?.breach_count ?? 0, pending: (prevSlaTopStats?.sla?.first_response?.pending ?? 0) + (prevSlaTopStats?.sla?.resolution?.pending ?? 0), actual_percent: prevSlaTopStats?.sla?.actual_percent ?? null }}
                  targetLabelText="100%"
                  description="First Response met + Resolution met / все SLA-метрики"
                />
                <SlaHeroCard title="First response SLA" metric={slaTopStats?.sla?.first_response} previous={prevSlaTopStats?.sla?.first_response} targetLabelText={targetLabel(slaTopStats?.sla?.first_response?.target_minutes, true)} description="First Response met / все First Response SLA" />
                <SlaHeroCard title="Resolution SLA" metric={slaTopStats?.sla?.resolution} previous={prevSlaTopStats?.sla?.resolution} targetLabelText={targetLabel(slaTopStats?.sla?.resolution?.target_minutes, true)} description="Resolution met / все Resolution SLA" />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, xs: 2, lg: 5 }} spacing={8}>
                <SlaTinyCard label="Цель SLA" value={slaPercent(slaTopStats?.sla?.target_percent)} delta={slaDeltaText(slaTopStats?.sla?.target_percent, prevSlaTopStats?.sla?.target_percent)} />
                <SlaTinyCard label="First response/Resolution target" value={targetLabel(slaTopStats?.sla?.first_response?.target_minutes || slaTopStats?.sla?.resolution?.target_minutes)} delta={slaDeltaText(slaTopStats?.sla?.first_response?.target_minutes, prevSlaTopStats?.sla?.first_response?.target_minutes, 'minutes')} />
                <SlaTinyCard label="Обращения в расчёте" value={formatNumber(slaTopStats?.sla?.tickets_total)} delta={slaDeltaText(slaTopStats?.sla?.tickets_total, prevSlaTopStats?.sla?.tickets_total, 'count')} />
                <SlaTinyCard label="SLA-метрики" value={formatNumber((slaTopStats?.sla?.first_response?.total ?? 0) + (slaTopStats?.sla?.resolution?.total ?? 0))} delta={slaDeltaText((slaTopStats?.sla?.first_response?.total ?? 0) + (slaTopStats?.sla?.resolution?.total ?? 0), (prevSlaTopStats?.sla?.first_response?.total ?? 0) + (prevSlaTopStats?.sla?.resolution?.total ?? 0), 'count')} />
                <SlaTinyCard label="Обращения с нарушением" value={formatNumber(slaTopStats?.sla?.breach_count)} delta={slaDeltaText(slaTopStats?.sla?.breach_count, prevSlaTopStats?.sla?.breach_count, 'count')} danger />
              </SimpleGrid>

              <SlaSectionCard title="Детализация SLA" right={<Group gap={18}><Group gap={6}><Box w={7} h={7} bg={SLA_BLUE} style={{ borderRadius: 99 }} /><Text size="xs">В SLA</Text></Group><Group gap={6}><Box w={7} h={7} bg={SLA_RED} style={{ borderRadius: 99 }} /><Text size="xs">Нарушено</Text></Group><Group gap={6}><Box w={7} h={7} bg="#D8DBE6" style={{ borderRadius: 99 }} /><Text size="xs">В ожидании</Text></Group></Group>}>
                <SlaDetailTable first={slaTopStats?.sla?.first_response} resolution={slaTopStats?.sla?.resolution} />
              </SlaSectionCard>
            </Stack>
          </Box>

          <SlaSectionCard
            title="Изменение SLA во времени"
            subtitle="Изменение в течение выбранного периода"
            right={<Group gap={12}><SegmentedControl size="xs" value={slaChartMetric} onChange={(value) => setSlaChartMetric(value as typeof slaChartMetric)} data={[{ label: 'Actual SLA', value: 'actual' }, { label: 'Resolution SLA', value: 'resolution' }, { label: 'First response SLA', value: 'first_response' }]} styles={{ root: { background: '#F5F6FA', borderRadius: 8, padding: 4 }, indicator: { background: '#EEF0FF', borderRadius: 6, boxShadow: 'none' }, label: { fontSize: 12, fontWeight: 700, color: SLA_BLUE, paddingInline: 28 } }} /><SlaFilterStrip {...slaLocalFilterProps('timeline')} /></Group>}
          >
            <Box pos="relative">
              {slaSectionLoading.timeline ? <BlockLoader label="Загружаем график SLA" /> : null}
              <Stack gap="sm" opacity={slaSectionLoading.timeline ? 0.45 : 1}>
                <SlaSummaryBand items={[
                  { label: 'Текущий SLA', value: slaPercent(slaTimelineCurrentActual) },
                  { label: 'Цель', value: '100%' },
                  { label: 'Отклонение', value: slaTimelineCurrentActual == null ? '—' : `${(slaTimelineCurrentActual - 100).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} п.п.`, accent: true },
                  { label: 'К предыдущему периоду', value: slaDeltaText(slaTimelineCurrentActual, slaTimelinePreviousActual) },
                ]} />
                <Box h={270} p="sm" style={{ border: `1px solid ${SLA_BORDER}`, borderRadius: 8 }}>
                  <Line data={slaLineData} options={slaLineOptions} />
                </Box>
              </Stack>
            </Box>
          </SlaSectionCard>

          <SlaSectionCard title="SLA по продуктам" subtitle="Данные по конкретным продуктам" right={<SlaFilterStrip {...slaLocalFilterProps('product')} showSort />}>
            <Box pos="relative">
              {slaSectionLoading.product ? <BlockLoader label="Загружаем продукты SLA" /> : null}
              <Stack gap="sm" opacity={slaSectionLoading.product ? 0.45 : 1}>
                <SlaSummaryBand items={[{ label: 'Нарушений', value: formatNumber(slaProductStats?.sla?.by_product?.reduce((sum, row) => sum + Number(row.breached ?? 0), 0)) }, { label: 'Главный источник', value: `${slaProductStats?.sla?.top_sources?.product?.label || '—'} · ${slaPercent(slaProductStats?.sla?.top_sources?.product?.actual_percent)}`, accent: true }]} />
                <SlaDynamicsTable rows={slaProductStats?.sla?.by_product || []} kind="product" />
              </Stack>
            </Box>
          </SlaSectionCard>

          <SlaSectionCard title="SLA по каналам" subtitle="Я пока не знаю, что здесь написать" right={<SlaFilterStrip {...slaLocalFilterProps('channel')} />}>
            <Box pos="relative">
              {slaSectionLoading.channel ? <BlockLoader label="Загружаем каналы SLA" /> : null}
              <Stack gap="sm" opacity={slaSectionLoading.channel ? 0.45 : 1}>
                <SlaSummaryBand items={[{ label: 'Нарушений', value: formatNumber(slaChannelStats?.sla?.by_channel?.reduce((sum, row) => sum + Number(row.breached ?? 0), 0)) }, { label: 'Главный источник', value: `${slaChannelStats?.sla?.top_sources?.channel?.label || '—'} · ${slaPercent(slaChannelStats?.sla?.top_sources?.channel?.actual_percent)}`, accent: true }]} />
                <SlaDynamicsTable rows={slaChannelStats?.sla?.by_channel || []} kind="channel" />
              </Stack>
            </Box>
          </SlaSectionCard>

          <SlaSectionCard title="SLA по очередям" subtitle="Где накапливаются ожидание, риск и просрочки" right={<SlaFilterStrip {...slaLocalFilterProps('queue')} showSort />}>
            <Box pos="relative">
              {slaSectionLoading.queue ? <BlockLoader label="Загружаем очереди SLA" /> : null}
              <Stack gap="sm" opacity={slaSectionLoading.queue ? 0.45 : 1}>
                <SlaSummaryBand items={[{ label: 'Очередей', value: formatNumber(slaQueueStats?.sla?.by_queue?.length) }, { label: 'В работе', value: formatNumber(slaQueueStats?.sla?.in_work) }, { label: 'Под риском', value: formatNumber(slaQueueStats?.sla?.at_risk) }, { label: 'Просрочено', value: formatNumber(slaQueueStats?.sla?.breach_count) }, { label: 'Главный источник', value: slaQueueStats?.sla?.top_sources?.queue?.label || '—', accent: true }]} />
                <SlaDynamicsTable rows={slaQueueStats?.sla?.by_queue || []} kind="queue" />
              </Stack>
            </Box>
          </SlaSectionCard>

          <SlaSectionCard title="SLA по агентам" subtitle="Распределение просрочек и нагрузки по сотрудникам" right={<SlaFilterStrip {...slaLocalFilterProps('agent')} showSort />}>
            <Box pos="relative">
              {slaSectionLoading.agent ? <BlockLoader label="Загружаем агентов SLA" /> : null}
              <Stack gap="sm" opacity={slaSectionLoading.agent ? 0.45 : 1}>
                <SlaSummaryBand items={[{ label: 'Агентов', value: formatNumber(slaAgentRows.length) }, { label: 'Обработано за период', value: formatNumber(slaAgentProcessed) }, { label: 'В работе', value: formatNumber(slaAgentInWork) }, { label: 'Под риском', value: formatNumber(slaAgentAtRisk) }, { label: 'Просрочено', value: formatNumber(slaAgentBreached) }, { label: 'Риск', value: slaAgentRisk?.label || '—', accent: true }]} />
                <SlaDynamicsTable rows={slaAgentRows} kind="agent" />
              </Stack>
            </Box>
          </SlaSectionCard>
        </Stack>
        ) : null}

        {analyticsTab === 'ai-ops' ? (
        <Stack gap="md">
          <Box pos="relative">
            {aiOpsLoading ? <BlockLoader label="Загружаем показатели Мозгобота" /> : null}
            <SimpleGrid cols={{ base: 1, xs: 2, lg: 5 }} spacing="sm" opacity={aiOpsLoading ? 0.45 : 1}>
            <MainInfoCard
              label="AI-проекты: всего обращений"
              delta={compactChangeLine(aiOps?.total_tickets, prevAiOps?.total_tickets, true)}
              tooltip={(
                <AnalyticsTooltipContent
                  title="AI-проекты: всего обращений"
                  rows={[
                    { label: 'Текущий период', value: formatNumber(aiOps?.total_tickets), tone: 'primary' },
                    { label: 'Предыдущий период', value: formatNumber(prevAiOps?.total_tickets), tone: 'muted' },
                    { label: 'База без аварий', value: formatNumber(aiOps?.non_emergency_tickets) },
                    { label: 'Аварийных', value: formatNumber(aiOps?.emergency_tickets) },
                    { label: 'Как считать', value: 'Уникальные обращения с действиями Мозгобота за период и обращения, оставшиеся у него в работе. Проценты считаются от этой базы без аварий.' },
                  ]}
                />
              )}
              value={formatNumber(aiOps?.total_tickets)}
            />
            <MainInfoCard
              label="Передано оператору"
              value={`${formatNumber(aiOps?.ai_transferred)} (${formatPercent(aiOps?.ai_transferred_pct_of_total)})`}
              delta={compactChangeLine(aiOps?.ai_transferred, prevAiOps?.ai_transferred, true)}
              tooltip={(
                <AnalyticsTooltipContent
                  title="Передано оператору"
                  rows={[
                    { label: 'Текущий период', value: `${formatNumber(aiOps?.ai_transferred)} (${formatPercent(aiOps?.ai_transferred_pct_of_total)})`, tone: 'primary' },
                    { label: 'Предыдущий период', value: `${formatNumber(prevAiOps?.ai_transferred)} (${formatPercent(prevAiOps?.ai_transferred_pct_of_total)})`, tone: 'muted' },
                    { label: 'Правило', value: 'Меньше передач оператору — лучше для самостоятельной работы Мозгобота.' },
                  ]}
                />
              )}
            />
            <MainInfoCard
              label="Решено / закрыто AI"
              value={`${formatNumber(aiOps?.ai_resolved_closed)} (${formatPercent(aiOps?.ai_resolved_closed_pct_of_total)})`}
              tone="success"
              delta={compactChangeLine(aiOps?.ai_resolved_closed_pct_of_total, prevAiOps?.ai_resolved_closed_pct_of_total, false)}
              tooltip={(
                <AnalyticsTooltipContent
                  title="Решено / закрыто AI"
                  rows={[
                    { label: 'Текущий период', value: `${formatNumber(aiOps?.ai_resolved_closed)} (${formatPercent(aiOps?.ai_resolved_closed_pct_of_total)})`, tone: 'primary' },
                    { label: 'Предыдущий период', value: `${formatNumber(prevAiOps?.ai_resolved_closed)} (${formatPercent(prevAiOps?.ai_resolved_closed_pct_of_total)})`, tone: 'muted' },
                    { label: 'Сравнение', value: 'Динамика считается по проценту самостоятельных решений, а не по количеству обращений.' },
                    { label: 'Как считать', value: 'Обращения, назначенные Мозгоботу: закрытые по дате закрытия и остающиеся в статусе «Решено» по дате решения в выбранном периоде. Автор действия в журнале не влияет на отбор.' },
                  ]}
                />
              )}
            />
            <MainInfoCard
              label="В работе у AI"
              value={`${formatNumber(aiOps?.ai_in_progress)} (${formatPercent(aiOps?.ai_in_progress_pct_of_total)})`}
              delta={compactChangeLine(aiOps?.ai_in_progress, prevAiOps?.ai_in_progress, true)}
              tooltip={(
                <AnalyticsTooltipContent
                  title="В работе у AI"
                  rows={[
                    { label: 'Текущий период', value: `${formatNumber(aiOps?.ai_in_progress)} (${formatPercent(aiOps?.ai_in_progress_pct_of_total)})`, tone: 'primary' },
                    { label: 'Предыдущий период', value: `${formatNumber(prevAiOps?.ai_in_progress)} (${formatPercent(prevAiOps?.ai_in_progress_pct_of_total)})`, tone: 'muted' },
                    { label: 'Как считать', value: 'Обращения, сейчас назначенные Мозгоботу и ещё не решённые или закрытые, созданные до конца выбранного периода. Дата создания до начала периода не исключает активное обращение.' },
                  ]}
                />
              )}
            />
            <MainInfoCard
              label="Watchdog: в повторе"
              value={formatNumber(aiOps?.watchdog_requeue_tickets_current)}
              delta={compactChangeLine(aiOps?.watchdog_requeue_tickets_current, prevAiOps?.watchdog_requeue_tickets_current, true)}
              tooltip={(
                <AnalyticsTooltipContent
                  title="Watchdog: в повторе"
                  rows={[
                    { label: 'Текущий период', value: formatNumber(aiOps?.watchdog_requeue_tickets_current), tone: 'primary' },
                    { label: 'Предыдущий период', value: formatNumber(prevAiOps?.watchdog_requeue_tickets_current), tone: 'muted' },
                    { label: 'Как считать', value: 'Текущий срез обращений, у которых уже есть счетчик повторной постановки watchdog.' },
                  ]}
                />
              )}
            />
            </SimpleGrid>
          </Box>

          <Group align="stretch" gap="md" wrap="wrap">
            <AnalyticsChartCard
              title="Мозгобот: причины передач оператору"
              summary={aiTransferredSummary}
              topNotice="Меньше передач оператору — лучше для самостоятельной работы Мозгобота."
              loading={aiOpsLoading}
              loadingLabel="Загружаем причины передач Мозгобота"
              right={aiReasonRows.map((row) => (
                <LegendAiReasonRow
                  key={row.key}
                  reasonKey={row.key as keyof typeof aiReasonHints}
                  name={row.label}
                  value={row.current}
                  previous={row.previous}
                  pctTransfers={row.pctTransfers}
                  pctTotal={row.pctTotal}
                  onClick={customPeriodPending ? undefined : () => setAiReasonModal({ reason: row.key as AiReasonKey, label: row.label })}
                />
              ))}
            >
              <Bar data={aiReasonChartData} options={aiReasonChartOptions} />
            </AnalyticsChartCard>

            <AnalyticsChartCard
              title="Мозгобот по проектам"
              summary={aiInProgressSummary}
              topNotice={aiProjectRows.length > 10 ? `На графике показаны топ-10 проектов по базе без аварий из ${aiProjectRows.length}. Внутри каждого проекта база раскладывается на: передано оператору, решено AI и еще в работе у AI.` : 'Внутри каждого проекта база без аварий раскладывается на: передано оператору, решено AI и еще в работе у AI.'}
              loading={aiOpsLoading}
              loadingLabel="Загружаем аналитику Мозгобота по проектам"
              right={aiProjectRows.map((row, index) => {
                const project = projects.find((p) => p.id === row.project_id)
                const name = project?.name || row.project_name || '—'
                const color = project?.color || operatorColors[index % operatorColors.length]
                return (
                <LegendAiProjectRow
                  key={row.project_id}
                  name={name}
                  color={color}
                  imageUrl={project?.icon_url ? resolveUploadsUrl(project.icon_url) : undefined}
                  emoji={project?.icon && !project.icon_url ? project.icon : undefined}
                  row={row}
                  previous={prevAiProjectMap.get(row.project_id)}
                />
                )
              })}
            >
              <Stack gap={4} h="100%">
                <Box style={{ flex: '1 1 auto', minHeight: 0 }}>
                  <Bar ref={aiProjectChartRef} data={aiProjectChartData} options={aiProjectChartOptions} />
                </Box>
                <AxisIconRow
                  chartRef={aiProjectChartRef}
                  items={topAiProjects.map((row, index) => {
                    const project = projects.find((p) => p.id === row.project_id)
                    const name = project?.name || row.project_name || '—'
                    const color = project?.color || operatorColors[index % operatorColors.length]
                    const previous = prevAiProjectMap.get(row.project_id)
                    return {
                      key: row.project_id || `${name}-${index}`,
                      name,
                      color,
                      imageUrl: project?.icon_url ? resolveUploadsUrl(project.icon_url) : undefined,
                      emoji: project?.icon && !project.icon_url ? project.icon : undefined,
                      tooltip: <AnalyticsTooltipContent title={name} rows={[
                        { label: 'Всего обращений', value: formatNumber(row.total_tickets), tone: 'primary' },
                        { label: 'База без аварий', value: formatNumber(row.non_emergency_tickets) },
                        { label: 'Передано', value: `${formatNumber(row.ai_transferred)} (${formatPercent(row.ai_transferred_pct_of_total)})` },
                        { label: 'Решено AI', value: `${formatNumber(row.ai_resolved_closed)} (${formatPercent(row.ai_resolved_closed_pct_of_total)})` },
                        { label: 'В работе у AI', value: `${formatNumber(row.ai_in_progress)} (${formatPercent(row.ai_in_progress_pct_of_total)})` },
                        { label: 'Предыдущий период', value: formatNumber(previous?.non_emergency_tickets), tone: 'muted' },
                      ]} />,
                    }
                  })}
                />
              </Stack>
            </AnalyticsChartCard>

          </Group>

          <Group align="stretch" gap="md" wrap="wrap">
            <AnalyticsChartCard
              title="Мозгобот: источники обращений"
              summary={aiSourceSummary}
              topNotice="Поток обращений, которые попали в аналитику Мозгобота, по каналам: Telegram, виджет, почта и другие источники."
              loading={aiOpsLoading}
              loadingLabel="Загружаем источники Мозгобота"
              right={aiSourceRows.map((row, index) => {
                const value = Number(row.total) || 0
                const previous = prevAiSourceMap.get(channelSourceKey(row)) || 0
                const pct = totalAiSources > 0 ? ((value / totalAiSources) * 100).toFixed(1) : '0'
                return (
                  <LegendSourceRow
                    key={channelSourceKey(row)}
                    name={channelSourceLabel(row.channel_type)}
                    color={sourceColors[index % sourceColors.length]}
                    value={value}
                    previous={previous}
                    pct={pct}
                  />
                )
              })}
            >
              <Bar data={aiSourceChartData} options={chartOptions} />
            </AnalyticsChartCard>

            <AnalyticsChartCard
              title="Мозгобот: источники по проектам"
              summary={aiProjectSourceSummary}
              topNotice={aiProjectSourceData.projectRows.length > 10 ? 'На графике показаны топ-10 AI-проектов по потоку обращений, внутри каждого столбца — источники.' : 'Внутри каждого AI-проекта показан поток обращений по источникам.'}
              loading={aiOpsLoading}
              loadingLabel="Загружаем источники Мозгобота по проектам"
              legendItems={[...aiProjectSourceData.sourceTotals.map((row) => ({ label: channelSourceLabel(row.channel_type), color: row.color })), { label: 'предыдущий период', color: previousColor }]}
              right={aiProjectSourceData.projectRows.map((row, index) => {
                const project = projects.find((item) => item.id === row.project_id)
                const name = project?.name || row.project_name || '—'
                const pct = aiProjectSourceData.total > 0 ? ((row.total / aiProjectSourceData.total) * 100).toFixed(1) : '0'
                return (
                  <LegendSourceRow
                    key={row.project_id}
                    name={name}
                    color={project?.color || operatorColors[index % operatorColors.length]}
                    avatar
                    imageUrl={project?.icon_url ? resolveUploadsUrl(project.icon_url) : undefined}
                    emoji={project?.icon && !project.icon_url ? project.icon : undefined}
                    value={row.total}
                    previous={row.previous}
                    pct={pct}
                  />
                )
              })}
            >
              <Stack gap={4} h="100%">
                <Box style={{ flex: '1 1 auto', minHeight: 0 }}>
                  <Bar ref={aiProjectSourceChartRef} data={aiProjectSourceData.chartData} options={stackedProjectSourceChartOptions} />
                </Box>
                <AxisIconRow
                  chartRef={aiProjectSourceChartRef}
                  items={aiProjectSourceData.projectRows.map((row, index) => {
                    const project = projects.find((item) => item.id === row.project_id)
                    const name = project?.name || row.project_name || '—'
                    return {
                      key: row.project_id,
                      name,
                      color: project?.color || operatorColors[index % operatorColors.length],
                      imageUrl: project?.icon_url ? resolveUploadsUrl(project.icon_url) : undefined,
                      emoji: project?.icon && !project.icon_url ? project.icon : undefined,
                      tooltip: <AnalyticsTooltipContent title={name} rows={[{ label: 'Текущий период', value: formatNumber(row.total), tone: 'primary' }, { label: 'Предыдущий период', value: formatNumber(row.previous), tone: 'muted' }]} />,
                    }
                  })}
                />
              </Stack>
            </AnalyticsChartCard>
          </Group>

        </Stack>
        ) : null}

        {analyticsTab === 'overview' ? (
        <>
        <Group align="stretch" gap="md" wrap="wrap">
          <AnalyticsChartCard title="КТУ операторов" titleHint={<Stack gap={6}>
            <Text size="xs">КТУ считается по фактическому рабочему времени из табеля: время смены минус перерывы. Если фактической смены нет, используется график.</Text>
            <Text size="xs">В completion идут только закрытые обращения и обращения в resolved, которые пережили окно автозакрытия без ответа клиента.</Text>
            <Text size="xs">Свежий resolved после ответа оператора, где еще ждут клиента, не считается полным решением и остается только в ответах и диалогах.</Text>
            <Text size="xs">Помощь в чужом тикете имеет разные веса: просто ответ минимален, доведение чужого тикета до решения и закрытия весит заметно выше.</Text>
            <Text size="xs">Штрафы: переоткрытие после решения, повторное обращение того же клиента в том же проекте в течение 24 часов после closed, а также назначенные или делегированные тикеты без ответа по клиентскому запросу.</Text>
            <Text size="xs">Итог: completion + ответы + сложные диалоги + помощь в чужих тикетах, скорректированные коэффициентом скорости, делятся на эффективные часы. Больше КТУ — лучше.</Text>
          </Stack>} summary={operatorKtuSummary} topNotice={operatorKtuSelectionWarning || 'КТУ показывает полезный результат конкретного оператора на 1 фактический рабочий час: реальные завершения, ответы, сложные диалоги и помощь в чужих тикетах с учетом скорости, перерывов и штрафов за возвраты и игнорирование назначенных обращений. Больше КТУ — лучше.'} loading={operatorKtuLoading} loadingLabel="Загружаем КТУ операторов" right={ktuOperators.map((op, index) => {
            const id = operatorKey(op)
            const name = op.operator_name || op.operator_email || '—'
            const value = Number(op.ktu) || 0
            const previous = prevOperatorKtuMap.get(op.operator_id || op.operator_name || '') || 0
            const color = operatorColors[index % operatorColors.length]
            return <LegendKtuRow key={id} name={name} color={color} imageUrl={resolveOperatorAvatarSrc(op)} value={value} previous={previous} selected={activeOperatorKtuChartIds.includes(id)} onToggle={() => toggleOperatorKtuInChart(id)} />
          })}>
            <Stack gap={4} h="100%">
              <Box style={{ flex: '1 1 auto', minHeight: 0 }}>
                <Bar ref={operatorKtuChartRef} data={operatorKtuChartData} options={operatorKtuChartOptions} />
              </Box>
              <AxisIconRow
                chartRef={operatorKtuChartRef}
                items={topOperatorKtu.map((op, index) => {
                  const name = op.operator_name || op.operator_email || '—'
                  const value = Number(op.ktu) || 0
                  const previous = prevOperatorKtuMap.get(op.operator_id || op.operator_name || '') || 0
                  return {
                    key: op.operator_id || `${name}-${index}`,
                    name,
                    color: operatorColors[index % operatorColors.length],
                    imageUrl: resolveOperatorAvatarSrc(op),
                    tooltip: <AnalyticsTooltipContent title={name} rows={[
                      { label: 'КТУ', value: formatKtu(value), tone: 'primary' },
                      { label: 'Предыдущий период', value: formatKtu(previous), tone: 'muted' },
                      { label: 'База баллов', value: formatKtu(op.ktu_base_points) },
                      { label: 'Коэф. скорости', value: formatKtu(op.ktu_speed_factor) },
                      { label: 'Эффективные часы (минус перерывы)', value: formatNumber(Number(op.working_hours) || 0) },
                      { label: 'Завершено в КТУ', value: formatNumber(op.finished_tickets_for_ktu) },
                      { label: 'Тикетов с ответом', value: formatNumber(op.replied_tickets) },
                      { label: 'Всего ответов', value: formatNumber(op.reply_messages) },
                      { label: 'Сложные диалоги', value: formatNumber(op.complex_tickets) },
                      { label: 'Чужие: ответил', value: formatNumber(op.foreign_replied_tickets) },
                      { label: 'Чужие: решено', value: formatNumber(op.foreign_resolved_tickets) },
                      { label: 'Чужие: закрыл', value: formatNumber(op.foreign_closed_after_resolved_tickets) },
                      { label: 'Переоткрыто после решения', value: formatNumber(op.resolved_reopened_count) },
                      { label: 'Повтор после closed за 24ч', value: formatNumber(op.repeated_after_close_24h_count) },
                      { label: 'Свои назначенные без ответа', value: formatNumber(op.own_pending_reply_penalty_count) },
                      { label: 'Делегированные без ответа', value: formatNumber(op.delegated_pending_reply_penalty_count) },
                    ]} />,
                  }
                })}
              />
            </Stack>
          </AnalyticsChartCard>

          <AnalyticsChartCard title="Время ответа и решения по проектам" summary={projectResponseSummary} topNotice={projectTimeSelectionWarning || `Ср. решение: ${projectCloseSummary.text.toLowerCase()}. Для времени меньше — лучше.`} loading={dashboardLoading} loadingLabel="Загружаем время по проектам" legendItems={[
            { label: 'среднее время ответа — текущий период', color: PROJECT_RESPONSE_TIME_SOLID },
            { label: 'среднее время решения — текущий период', color: PROJECT_RESOLUTION_TIME_SOLID },
            { label: 'предыдущий период', color: previousColor },
          ]} right={projectTimeRows.map((row) => {
            const id = productKey(row)
            const project = projects.find((p) => p.id === row.project_id)
            const name = project?.name || row.project_name || '—'
            const responseValue = Number(row.avg_first_response_minutes) || 0
            const responsePrevious = prevProjectResponseMap.get(row.project_id || '') || 0
            return <LegendKtuRow key={id} name={name} color={project?.color || '#3B82F6'} imageUrl={project?.icon_url ? resolveUploadsUrl(project.icon_url) : undefined} emoji={project?.icon && !project.icon_url ? project.icon : undefined} value={responseValue} previous={responsePrevious} selected={activeProjectTimeChartIds.includes(id)} onToggle={() => toggleProjectTimeInChart(id)} formatter={formatDurationClock} />
          })}>
            <Stack gap={4} h="100%">
              <Box style={{ flex: '1 1 auto', minHeight: 0 }}>
                <Bar ref={projectTimeChartRef} data={projectTimeChartData} options={projectTimeChartOptions} />
              </Box>
              <AxisIconRow
                chartRef={projectTimeChartRef}
                items={topProjectTime.map((row, index) => {
                  const project = projects.find((p) => p.id === row.project_id)
                  const name = project?.name || row.project_name || '—'
                  const responseValue = Number(row.avg_first_response_minutes) || 0
                  const responsePrevious = prevProjectResponseMap.get(row.project_id || '') || 0
                  const closeValue = Number(row.avg_close_minutes) || 0
                  const closePrevious = prevProjectCloseMap.get(row.project_id || '') || 0
                  return {
                    key: row.project_id || `${name}-${index}`,
                    name,
                    color: project?.color || '#3B82F6',
                    imageUrl: project?.icon_url ? resolveUploadsUrl(project.icon_url) : undefined,
                    emoji: project?.icon && !project.icon_url ? project.icon : undefined,
                    tooltip: <AnalyticsTooltipContent title={name} rows={[{ label: 'Ср. ответ', value: formatDurationClock(responseValue), tone: 'primary' }, { label: 'Пред. ответ', value: formatDurationClock(responsePrevious), tone: 'muted' }, { label: 'Ср. решение', value: formatDurationClock(closeValue) }, { label: 'Пред. решение', value: formatDurationClock(closePrevious), tone: 'muted' }]} />,
                  }
                })}
              />
            </Stack>
          </AnalyticsChartCard>
        </Group>
        <Group align="stretch" gap="md" wrap="wrap">
          <AnalyticsChartCard
            title="Источники обращений"
            summary={sourceSummary}
            topNotice="Поток обращений по каналам: Telegram, виджет, почта и другие подключенные источники."
            loading={dashboardLoading}
            loadingLabel="Загружаем источники обращений"
            right={overviewSourceRows.map((row, index) => {
              const value = Number(row.total) || 0
              const previous = prevOverviewSourceMap.get(channelSourceKey(row)) || 0
              const pct = totalOverviewSources > 0 ? ((value / totalOverviewSources) * 100).toFixed(1) : '0'
              return (
                <LegendSourceRow
                  key={channelSourceKey(row)}
                  name={channelSourceLabel(row.channel_type)}
                  color={sourceColors[index % sourceColors.length]}
                  value={value}
                  previous={previous}
                  pct={pct}
                />
              )
            })}
          >
            <Bar data={overviewSourceChartData} options={chartOptions} />
          </AnalyticsChartCard>

          <AnalyticsChartCard
            title="Источники по проектам"
            summary={projectSourceSummary}
            topNotice={overviewProjectSourceData.projectRows.length > 10 ? 'На графике показаны топ-10 проектов по потоку обращений, внутри каждого столбца — источники.' : 'Внутри каждого проекта показан поток обращений по источникам.'}
            loading={dashboardLoading}
            loadingLabel="Загружаем источники по проектам"
            legendItems={[...overviewProjectSourceData.sourceTotals.map((row) => ({ label: channelSourceLabel(row.channel_type), color: row.color })), { label: 'предыдущий период', color: previousColor }]}
            right={overviewProjectSourceData.projectRows.map((row, index) => {
              const project = projects.find((item) => item.id === row.project_id)
              const name = project?.name || row.project_name || '—'
              const pct = overviewProjectSourceData.total > 0 ? ((row.total / overviewProjectSourceData.total) * 100).toFixed(1) : '0'
              return (
                <LegendSourceRow
                  key={row.project_id}
                  name={name}
                  color={project?.color || operatorColors[index % operatorColors.length]}
                  avatar
                  imageUrl={project?.icon_url ? resolveUploadsUrl(project.icon_url) : undefined}
                  emoji={project?.icon && !project.icon_url ? project.icon : undefined}
                  value={row.total}
                  previous={row.previous}
                  pct={pct}
                />
              )
            })}
          >
            <Stack gap={4} h="100%">
              <Box style={{ flex: '1 1 auto', minHeight: 0 }}>
                <Bar ref={overviewProjectSourceChartRef} data={overviewProjectSourceData.chartData} options={stackedProjectSourceChartOptions} />
              </Box>
              <AxisIconRow
                chartRef={overviewProjectSourceChartRef}
                items={overviewProjectSourceData.projectRows.map((row, index) => {
                  const project = projects.find((item) => item.id === row.project_id)
                  const name = project?.name || row.project_name || '—'
                  return {
                    key: row.project_id,
                    name,
                    color: project?.color || operatorColors[index % operatorColors.length],
                    imageUrl: project?.icon_url ? resolveUploadsUrl(project.icon_url) : undefined,
                    emoji: project?.icon && !project.icon_url ? project.icon : undefined,
                    tooltip: <AnalyticsTooltipContent title={name} rows={[{ label: 'Текущий период', value: formatNumber(row.total), tone: 'primary' }, { label: 'Предыдущий период', value: formatNumber(row.previous), tone: 'muted' }]} />,
                  }
                })}
              />
            </Stack>
          </AnalyticsChartCard>
        </Group>
        </>
        ) : null}

        {analyticsTab === 'operator-times' ? (
        <Stack gap="md">
          <Box pos="relative">
            {operatorTimesLoading ? <BlockLoader label="Загружаем время операторов" /> : null}
            <SimpleGrid cols={{ base: 1, xs: 2, lg: 4 }} spacing="sm" opacity={operatorTimesLoading ? 0.45 : 1}>
              <MainInfoCard
                label="Ср. время назначения"
                value={avgAssignmentTimeSummary.current == null ? '—' : formatDurationClock(avgAssignmentTimeSummary.current)}
                delta={avgAssignmentTimeSummary.delta}
                tooltip={(
                  <AnalyticsTooltipContent
                    title="Среднее время назначения"
                    rows={[
                      { label: 'Текущий период', value: avgAssignmentTimeSummary.current == null ? '—' : formatDurationClock(avgAssignmentTimeSummary.current), tone: 'primary' },
                      { label: 'Предыдущий период', value: avgAssignmentTimeSummary.previous == null ? '—' : formatDurationClock(avgAssignmentTimeSummary.previous), tone: 'muted' },
                      { label: 'Формула', value: 'ticket_assigned.created_at − ticket.created_at' },
                      { label: 'Что это', value: 'Среднее время от создания обращения до назначения на оператора.' },
                    ]}
                  />
                )}
              />
              <MainInfoCard
                label="Ср. время решения"
                value={avgResolutionTimeSummary.current == null ? '—' : formatDurationClock(avgResolutionTimeSummary.current)}
                delta={avgResolutionTimeSummary.delta}
                tooltip={(
                  <AnalyticsTooltipContent
                    title="Среднее время решения"
                    rows={[
                      { label: 'Текущий период', value: avgResolutionTimeSummary.current == null ? '—' : formatDurationClock(avgResolutionTimeSummary.current), tone: 'primary' },
                      { label: 'Предыдущий период', value: avgResolutionTimeSummary.previous == null ? '—' : formatDurationClock(avgResolutionTimeSummary.previous), tone: 'muted' },
                      { label: 'Формула', value: 'resolved_at (или closed_at) − created_at' },
                      { label: 'Что это', value: 'Среднее по операторам время от создания обращения до решения.' },
                    ]}
                  />
                )}
              />
              <MainInfoCard
                label="Ср. время первого ответа"
                value={avgFirstResponseTimeSummary.current == null ? '—' : formatDurationClock(avgFirstResponseTimeSummary.current)}
                delta={avgFirstResponseTimeSummary.delta}
                tooltip={(
                  <AnalyticsTooltipContent
                    title="Среднее время первого ответа"
                    rows={[
                      { label: 'Текущий период', value: avgFirstResponseTimeSummary.current == null ? '—' : formatDurationClock(avgFirstResponseTimeSummary.current), tone: 'primary' },
                      { label: 'Предыдущий период', value: avgFirstResponseTimeSummary.previous == null ? '—' : formatDurationClock(avgFirstResponseTimeSummary.previous), tone: 'muted' },
                      { label: 'Формула', value: 'первый ответ оператора − назначение/перераспределение' },
                      { label: 'Что это', value: 'Среднее время первого ответа оператора в каждом сегменте ответственности.' },
                    ]}
                  />
                )}
              />
              <MainInfoCard
                label="Ср. время всех ответов"
                value={avgReplyTimeSummary.current == null ? '—' : formatDurationClock(avgReplyTimeSummary.current)}
                delta={avgReplyTimeSummary.delta}
                tooltip={(
                  <AnalyticsTooltipContent
                    title="Среднее время всех ответов"
                    rows={[
                      { label: 'Текущий период', value: avgReplyTimeSummary.current == null ? '—' : formatDurationClock(avgReplyTimeSummary.current), tone: 'primary' },
                      { label: 'Предыдущий период', value: avgReplyTimeSummary.previous == null ? '—' : formatDurationClock(avgReplyTimeSummary.previous), tone: 'muted' },
                      { label: 'Формула', value: 'В среднем от сообщения клиента до ответа оператора (по всем сообщениям)' },
                      { label: 'Что это', value: 'Среднее по операторам время ответа на все сообщения клиентов, а не только на первое.' },
                    ]}
                  />
                )}
              />
            </SimpleGrid>
          </Box>

          <SlaSectionCard
            title="Время операторов"
            subtitle="Среднее время назначения, первого ответа, всех ответов и решения"
            right={<Text size="sm" fw={700} c="#8190A7">{getDashboardPeriodLabel(period)}</Text>}
          >
            <Box pos="relative">
              {operatorTimesLoading ? <BlockLoader label="Загружаем таблицу операторов" /> : null}
              <Stack gap="sm" opacity={operatorTimesLoading ? 0.45 : 1}>
                <SlaSummaryBand items={[
                  { label: 'Операторов', value: formatNumber(visibleOperatorTimes.length) },
                  { label: 'Ср. назначение', value: avgAssignmentTimeSummary.current == null ? '—' : formatDurationClock(avgAssignmentTimeSummary.current) },
                  { label: 'Ср. первый ответ', value: avgFirstResponseTimeSummary.current == null ? '—' : formatDurationClock(avgFirstResponseTimeSummary.current) },
                  { label: 'Ср. решение', value: avgResolutionTimeSummary.current == null ? '—' : formatDurationClock(avgResolutionTimeSummary.current) },
                ]} />
                <Table.ScrollContainer minWidth={980}>
                  <Table withRowBorders={false} style={{ tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <Table.Thead bg="#EEF1FA">
                    <Table.Tr>
                      <Table.Th style={{ color: SLA_BLUE, textAlign: 'center', width: 260 }}>Оператор</Table.Th>
                      <Table.Th style={{ color: SLA_BLUE, textAlign: 'center' }}>Ср. время назначения</Table.Th>
                      <Table.Th style={{ color: SLA_BLUE, textAlign: 'center' }}>Ср. время первого ответа</Table.Th>
                      <Table.Th style={{ color: SLA_BLUE, textAlign: 'center' }}>Ср. время всех ответов</Table.Th>
                      <Table.Th style={{ color: SLA_BLUE, textAlign: 'center' }}>Ср. время решения</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {visibleOperatorTimes.length === 0 ? (
                      <Table.Tr bg="#F7F8FD">
                        <Table.Td colSpan={5}>
                          <Text size="sm" c="#8190A7" ta="center" py="md">Нет данных</Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      visibleOperatorTimes.map((op, index) => {
                        const previous = prevOperatorTimesMap.get(operatorKey(op))
                        return (
                          <Table.Tr key={operatorKey(op)} bg={index % 2 === 0 ? '#F7F8FD' : '#FFFFFF'}>
                            <Table.Td>
                              <Group gap={8} wrap="nowrap">
                                <Avatar src={resolveOperatorAvatarSrc(op)} radius="xl" size={28}>
                                  {(op.operator_name?.[0] || '—').toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Text fw={700} size="sm">{op.operator_name || op.operator_email || '—'}</Text>
                                  {op.operator_email && op.operator_name ? <Text size="xs" c="#69758A">{op.operator_email}</Text> : null}
                                </Box>
                              </Group>
                            </Table.Td>
                            <Table.Td ta="center"><OperatorTimeCell current={op.avg_assignment_minutes} previous={previous?.avg_assignment_minutes} /></Table.Td>
                            <Table.Td ta="center"><OperatorTimeCell current={op.avg_first_response_minutes} previous={previous?.avg_first_response_minutes} /></Table.Td>
                            <Table.Td ta="center"><OperatorTimeCell current={op.avg_reply_minutes} previous={previous?.avg_reply_minutes} /></Table.Td>
                            <Table.Td ta="center"><OperatorTimeCell current={op.avg_resolution_time_minutes} previous={previous?.avg_resolution_time_minutes} /></Table.Td>
                          </Table.Tr>
                        )
                      })
                    )}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
              </Stack>
            </Box>
          </SlaSectionCard>
        </Stack>
        ) : null}
      </Stack>
      {chartRowsTooltip.visible && chartRowsTooltip.data ? (
        <Box
          style={{
            position: 'fixed',
            left: Math.max(8, Math.min(chartRowsTooltip.viewportX + 14, Math.max(8, chartRowsTooltip.viewportWidth - ANALYTICS_TOOLTIP_WIDTH - 8))),
            top: Math.max(8, Math.min(chartRowsTooltip.viewportY - 110, Math.max(8, chartRowsTooltip.viewportHeight - chartRowsTooltip.height - 8))),
            zIndex: 5000,
            pointerEvents: 'none',
          }}
        >
          <ChartRowsTooltipContent data={chartRowsTooltip.data} />
        </Box>
      ) : null}
      </AnalyticsTooltipProvider>

      <OperatorTicketsModal opened={Boolean(operatorModal)} onClose={() => setOperatorModal(null)} operatorId={operatorModal?.operatorId || ''} block={operatorModal?.block || 'closed_tickets'} blockLabel={operatorModal?.label || 'Обращения'} analyticsParams={operatorDrilldownQuery as Record<string, string | number | boolean>} />
      <ProductTicketsModal opened={Boolean(productModal)} onClose={() => setProductModal(null)} project={productModal} periodParams={productDrilldownPeriodParams} withoutAI={productDrilldownWithoutAI} />
      <AiTransferTicketsModal opened={Boolean(aiReasonModal)} onClose={() => setAiReasonModal(null)} reason={aiReasonModal?.reason || 'transfer_other'} label={aiReasonModal?.label || 'Причина передачи'} analyticsParams={aiOpsDrilldownBaseQuery as Record<string, string | number | boolean>} />
    </PageShell>
  )
}
